/**
 * room/furniture.ts — Furniture Registry & Interaction
 *
 * Every piece of furniture in Hearth is a living thing. It can be sat on,
 * played, lit, dimmed, read from, cooked at, listened to. A room full of
 * static objects is a room full of dead weight. A room full of furniture
 * that responds to you — that is a home.
 *
 * This module defines:
 *   - The FurnitureType interface (what a piece of furniture IS)
 *   - A starting registry of ~30 furniture types
 *   - renderFurniture() — procedural pixel art for each type
 *
 * Rendering philosophy: charming, not detailed. Every sprite is 32-48px wide.
 * Colors are muted and warm. These are not photo-realistic objects — they are
 * the idea of objects, rendered with care.
 */

import type { MoodContribution } from "./mood.js";
import type { FurniturePlacement, WallSegment, Direction } from "../engine/state.js";
import { renderBoxAnimated } from "../assets/generated/BoxAnimated.js";
import { renderLeatherCouch } from "../assets/generated/LeatherCouch.js";
import { renderMacbook } from "../assets/generated/Macbook.js";
import { renderSpaceship } from "../assets/generated/Spaceship.js";
import { renderAudiRS5 } from "../assets/generated/AudiRS5.js";
import { renderBusterDrone } from "../assets/generated/BusterDrone.js";

// ─── Category ────────────────────────────────────────────────────────────────

export type FurnitureCategory =
  | "seating"     // chairs, sofas, benches
  | "table"       // tables, desks, counters
  | "storage"     // shelves, cabinets, chests
  | "lighting"    // lamps, candles, chandeliers
  | "decoration"  // art, rugs, curtains, plants
  | "tech"        // computers, screens, aeterna device
  | "music"       // instruments, speakers, record player
  | "game"        // arcade cabinets, chess boards, card tables
  | "kitchen"     // stove, fridge, counter, sink
  | "bathroom"    // toilet, sink, bath, mirror
  | "outdoor"     // trees, benches, fountains
  | "special";    // rare, seasonal, achievement items

// ─── Sprite Specification ─────────────────────────────────────────────────────

/**
 * Describes how to procedurally render a furniture type.
 * Actual drawing lives in renderFurniture(); this is the data shape.
 */
export interface FurnitureSprite {
  /** Pixel width of the rendered sprite */
  width: number;
  /** Pixel height of the rendered sprite */
  height: number;
  /**
   * Rendering variant key. The renderFurniture function switch-cases on this.
   * Keeps the sprite data declarative and the drawing logic co-located.
   */
  variant: string;
}

// ─── Material System ──────────────────────────────────────────────────────────
// Every physical object in Hearth has a material. Materials determine:
//   - Impact sounds when struck (combat, footsteps on top of)
//   - Interaction sounds (placing, moving, scraping)
//   - How objects resonate when toggled or activated
// This is the connective tissue that makes the world feel physically real.

export type MaterialType =
  | "wood"        // warm thock, creak, knock
  | "stone"       // heavy crack, scrape, deep thud
  | "metal"       // metallic ring, clang, resonance
  | "fabric"      // soft thump, rustle, muffled
  | "glass"       // bright clink, shatter, crystalline
  | "ceramic"     // hollow tap, bright break
  | "leather"     // soft creak, pat
  | "paper"       // rustle, crinkle, page turn
  | "crystal"     // bright ring, harmonic shimmer
  | "organic";    // dull thud, soft impact (plants, food)

// ─── Light & Sound Emission ───────────────────────────────────────────────────

export interface LightEmission {
  /** CSS hex color of the emitted light */
  color: string;
  /** Radius in tile units */
  radius: number;
  /** 0-1 intensity (affects mood computation and visual radius) */
  intensity: number;
}

export interface SoundEmission {
  /** Continuous ambient sound key when ON state, matched by audio layer */
  type: string;
  /** Radius in tile units at which the sound can be heard */
  radius: number;
  /** 0-1 base volume when at the source */
  volume: number;
  /** One-shot sound when toggled ON (e.g. lamp click, fireplace whoosh) */
  toggleOnSound?: string;
  /** One-shot sound when toggled OFF */
  toggleOffSound?: string;
  /** One-shot sound when placed in the room */
  placeSound?: string;
  /** One-shot sound when interacted with (non-toggle) */
  interactSound?: string;
  /** Whether this item supports channel selection (radio stations, etc.) */
  hasChannels?: boolean;
  /** Named channels available for selection */
  channels?: string[];
}

// ─── Furniture Type ───────────────────────────────────────────────────────────

export interface FurnitureType {
  id: string;
  name: string;
  category: FurnitureCategory;

  /** Tile footprint width (X axis) */
  tileWidth: number;
  /** Tile footprint height (Y axis) */
  tileHeight: number;

  /** True if characters can sit/rest on this */
  canSitOn: boolean;
  /** True if other furniture/items can be stacked on top */
  canPlaceOn: boolean;
  /** True if clicking produces an interaction */
  interactable: boolean;
  /** Interaction type key, consumed by interact/ module */
  interactionType?: string;

  /** How this piece shifts the room's overall mood */
  moodContribution: MoodContribution;

  /** If present, this piece emits light when active */
  lightEmission?: LightEmission;
  /** If present, this piece emits ambient sound when active */
  soundEmission?: SoundEmission;

  rarity: "common" | "uncommon" | "rare" | "legendary" | "seasonal";
  sprite: FurnitureSprite;

  /** Short flavor description shown in room info / Aeterna context */
  description: string;

  /** True for flat floor decorations (rugs, mats) — always drawn beneath standing entities */
  isFloor?: boolean;

  /** Physical material — determines impact/interaction sound character */
  material?: MaterialType;

  /** True if this asset was generated by hearth-asset-pipeline from a 3D model */
  pipelineGenerated?: boolean;
}

// ─── Registry ─────────────────────────────────────────────────────────────────

/**
 * The master furniture registry.
 *
 * To add a new furniture type:
 *   1. Add an entry here.
 *   2. Add a rendering case in renderFurniture() below.
 *   3. Done. No other wiring required.
 */
const FURNITURE_TYPES: FurnitureType[] = [
  // ── Seating ──────────────────────────────────────────────────────────────

  {
    id: "sofa_oak",
    name: "Oak Sofa",
    category: "seating",
    tileWidth: 2, tileHeight: 1,
    canSitOn: true, canPlaceOn: false, interactable: true,
    interactionType: "sit",
    moodContribution: { warmth: 0.2, coziness: 0.35, energy: -0.1, mystery: 0, nature: 0.05 },
    rarity: "common",
    description: "A wide, well-worn sofa. Clearly well-loved.",
    sprite: { width: 96, height: 64, variant: "sofa_oak" },
    material: "wood",
  },

  {
    id: "chair_wooden",
    name: "Wooden Chair",
    category: "seating",
    tileWidth: 1, tileHeight: 1,
    canSitOn: true, canPlaceOn: false, interactable: true,
    interactionType: "sit",
    moodContribution: { warmth: 0.05, coziness: 0.1, energy: 0, mystery: 0, nature: 0.05 },
    rarity: "common",
    description: "Simple. Honest. Reliable.",
    sprite: { width: 40, height: 52, variant: "chair_wooden" },
    material: "wood",
  },

  {
    id: "bed_cozy",
    name: "Cozy Bed",
    category: "seating",
    tileWidth: 2, tileHeight: 1,
    canSitOn: true, canPlaceOn: false, interactable: true,
    interactionType: "sleep",
    moodContribution: { warmth: 0.15, coziness: 0.4, energy: -0.25, mystery: 0, nature: 0 },
    rarity: "common",
    description: "Piled high with pillows. The kind of bed you sink into.",
    sprite: { width: 96, height: 72, variant: "bed_cozy" },
    material: "fabric",
  },

  {
    id: "hammock",
    name: "Hammock",
    category: "seating",
    tileWidth: 2, tileHeight: 1,
    canSitOn: true, canPlaceOn: false, interactable: true,
    interactionType: "sit",
    moodContribution: { warmth: 0.1, coziness: 0.3, energy: -0.15, mystery: 0, nature: 0.2 },
    rarity: "uncommon",
    description: "Strung between two poles, swaying gently.",
    sprite: { width: 88, height: 52, variant: "hammock" },
    material: "fabric",
  },

  // ── Tables ───────────────────────────────────────────────────────────────

  {
    id: "coffee_table",
    name: "Coffee Table",
    category: "table",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: true, interactable: false,
    moodContribution: { warmth: 0.05, coziness: 0.1, energy: 0, mystery: 0, nature: 0 },
    rarity: "common",
    description: "Low and sturdy. Good for cups of tea.",
    sprite: { width: 40, height: 24, variant: "coffee_table" },
    material: "wood",
  },

  {
    id: "desk_study",
    name: "Study Desk",
    category: "table",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: true, interactable: true,
    interactionType: "work",
    moodContribution: { warmth: 0.05, coziness: 0.1, energy: 0.1, mystery: 0.05, nature: 0 },
    rarity: "common",
    description: "A proper writing desk. Papers everywhere.",
    sprite: { width: 44, height: 32, variant: "desk_study" },
    material: "wood",
  },

  {
    id: "small_table",
    name: "Small Table",
    category: "table",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: true, interactable: false,
    moodContribution: { warmth: 0.02, coziness: 0.05, energy: 0, mystery: 0, nature: 0 },
    rarity: "common",
    description: "A little side table. Useful everywhere.",
    sprite: { width: 36, height: 28, variant: "small_table" },
    material: "wood",
  },

  // ── Storage ───────────────────────────────────────────────────────────────

  {
    id: "bookshelf",
    name: "Bookshelf",
    category: "storage",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "bookshelf",
    moodContribution: { warmth: 0.1, coziness: 0.2, energy: 0.05, mystery: 0.2, nature: 0 },
    rarity: "common",
    description: "Packed with books. You could spend hours here.",
    sprite: { width: 48, height: 80, variant: "bookshelf" },
    material: "wood",
  },

  {
    id: "chest_old",
    name: "Old Chest",
    category: "storage",
    tileWidth: 1, tileHeight: 1,
    canSitOn: true, canPlaceOn: false, interactable: true,
    interactionType: "storage",
    moodContribution: { warmth: 0.05, coziness: 0.1, energy: 0, mystery: 0.15, nature: 0 },
    rarity: "uncommon",
    description: "Brass-latched and heavy. Who knows what's inside.",
    sprite: { width: 40, height: 36, variant: "chest_old" },
    material: "wood",
  },

  // ── Lighting ──────────────────────────────────────────────────────────────

  {
    id: "floor_lamp",
    name: "Floor Lamp",
    category: "lighting",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "toggle",
    moodContribution: { warmth: 0.15, coziness: 0.15, energy: 0.05, mystery: 0, nature: 0 },
    lightEmission: { color: "#ffd580", radius: 3, intensity: 0.7 },
    soundEmission: { type: "", radius: 0, volume: 0, toggleOnSound: "sfx_lamp_click", toggleOffSound: "sfx_lamp_click" },
    rarity: "common",
    description: "A tall lamp with a warm cloth shade.",
    sprite: { width: 28, height: 60, variant: "floor_lamp" },
    material: "metal",
  },

  {
    id: "candle",
    name: "Candle",
    category: "lighting",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "toggle",
    moodContribution: { warmth: 0.1, coziness: 0.2, energy: -0.05, mystery: 0.1, nature: 0 },
    lightEmission: { color: "#ffb347", radius: 1.5, intensity: 0.35 },
    rarity: "common",
    description: "Flickers in the dark. Calls to mind old things.",
    sprite: { width: 16, height: 28, variant: "candle" },
    material: "ceramic",
  },

  {
    id: "fireplace",
    name: "Fireplace",
    category: "lighting",
    tileWidth: 2, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "toggle",
    moodContribution: { warmth: 0.45, coziness: 0.4, energy: -0.05, mystery: 0.1, nature: 0 },
    lightEmission: { color: "#ff8c30", radius: 4, intensity: 0.9 },
    soundEmission: { type: "sfx_fire_crackle", radius: 4, volume: 0.5, toggleOnSound: "sfx_fire_ignite", toggleOffSound: "sfx_fire_extinguish" },
    rarity: "uncommon",
    description: "The heart of any room worth the name.",
    sprite: { width: 80, height: 56, variant: "fireplace" },
    material: "stone",
  },

  {
    id: "chandelier",
    name: "Chandelier",
    category: "lighting",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "toggle",
    moodContribution: { warmth: 0.1, coziness: 0.1, energy: 0.15, mystery: 0.2, nature: 0 },
    lightEmission: { color: "#ffe4b0", radius: 5, intensity: 0.85 },
    rarity: "uncommon",
    description: "Crystal drops scatter warm light across the ceiling.",
    sprite: { width: 44, height: 48, variant: "chandelier" },
    material: "crystal",
  },

  // ── Decoration ────────────────────────────────────────────────────────────

  {
    id: "potted_plant",
    name: "Potted Plant",
    category: "decoration",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "water",
    moodContribution: { warmth: 0.05, coziness: 0.15, energy: 0.05, mystery: 0, nature: 0.35 },
    rarity: "common",
    description: "A leafy companion. Needs water on Tuesdays.",
    sprite: { width: 32, height: 44, variant: "potted_plant" },
    material: "ceramic",
  },

  {
    id: "rug_woven",
    name: "Woven Rug",
    category: "decoration",
    tileWidth: 2, tileHeight: 2,
    canSitOn: false, canPlaceOn: true, interactable: false,
    moodContribution: { warmth: 0.1, coziness: 0.3, energy: 0, mystery: 0.05, nature: 0.05 },
    rarity: "common",
    description: "Thick and warm underfoot. Changes the sound of the room.",
    sprite: { width: 96, height: 52, variant: "rug_woven" },
    isFloor: true,
    material: "fabric",
  },

  {
    id: "window_frame",
    name: "Window",
    category: "decoration",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: false,
    moodContribution: { warmth: 0.05, coziness: 0.1, energy: 0.15, mystery: 0, nature: 0.1 },
    lightEmission: { color: "#c8e8ff", radius: 3, intensity: 0.6 },
    rarity: "common",
    description: "Lets in the outside world. Time of day changes everything.",
    sprite: { width: 44, height: 52, variant: "window_frame" },
    material: "glass",
  },

  {
    id: "fish_tank",
    name: "Fish Tank",
    category: "decoration",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "observe",
    moodContribution: { warmth: 0, coziness: 0.2, energy: 0.05, mystery: 0.1, nature: 0.3 },
    lightEmission: { color: "#40c0ff", radius: 1.5, intensity: 0.3 },
    soundEmission: { type: "sfx_water_bubble", radius: 2, volume: 0.15, interactSound: "sfx_glass_tap" },
    rarity: "uncommon",
    description: "Little fish going about their day, unbothered.",
    sprite: { width: 44, height: 48, variant: "fish_tank" },
    material: "glass",
  },

  {
    id: "wall_art",
    name: "Wall Art",
    category: "decoration",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "customize",
    moodContribution: { warmth: 0.05, coziness: 0.1, energy: 0.05, mystery: 0.1, nature: 0 },
    rarity: "common",
    description: "A framed piece. Customizable — your art, your room.",
    sprite: { width: 36, height: 40, variant: "wall_art" },
    material: "wood",
    soundEmission: { type: "sfx_wood_impact", radius: 1, volume: 0.2 },
  },

  // ── Tech ──────────────────────────────────────────────────────────────────

  {
    id: "aeterna_device",
    name: "Aeterna Device",
    category: "tech",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "aeterna",
    moodContribution: { warmth: 0.1, coziness: 0.05, energy: 0.1, mystery: 0.3, nature: 0 },
    lightEmission: { color: "#a0d8ef", radius: 2, intensity: 0.4 },
    rarity: "legendary",
    description: "The Aeterna. Your Digital Intelligence partner. It knows your name.",
    sprite: { width: 32, height: 48, variant: "aeterna_device" },
    material: "crystal",
  },

  {
    id: "computer_desk",
    name: "Personal Computer",
    category: "tech",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "computer",
    moodContribution: { warmth: 0, coziness: 0, energy: 0.2, mystery: 0.05, nature: 0 },
    lightEmission: { color: "#8080ff", radius: 1.5, intensity: 0.25 },
    rarity: "common",
    description: "Screen glow in the dark. Connectivity and isolation at once.",
    sprite: { width: 36, height: 44, variant: "computer_desk" },
    material: "metal",
  },

  // ── Music ─────────────────────────────────────────────────────────────────

  {
    id: "record_player",
    name: "Record Player",
    category: "music",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "music",
    moodContribution: { warmth: 0.2, coziness: 0.2, energy: 0.1, mystery: 0.05, nature: 0 },
    soundEmission: {
      type: "music_record_player", radius: 6, volume: 0.5,
      toggleOnSound: "sfx_needle_drop", toggleOffSound: "sfx_needle_lift",
      hasChannels: true,
      channels: ["Ember Jazz", "Midnight Lounge", "Hearthstone Classical", "Lo-fi Chill", "Dawn Acoustic"],
    },
    rarity: "uncommon",
    description: "Warm analog sound. The room feels different when it's spinning.",
    sprite: { width: 40, height: 28, variant: "record_player" },
    material: "wood",
  },

  {
    id: "piano_upright",
    name: "Upright Piano",
    category: "music",
    tileWidth: 2, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "piano",
    moodContribution: { warmth: 0.15, coziness: 0.2, energy: 0.15, mystery: 0.15, nature: 0 },
    soundEmission: { type: "sfx_piano", radius: 8, volume: 0.7, interactSound: "sfx_piano_chord" },
    rarity: "rare",
    description: "Dark lacquered wood. Plays whatever you put in.",
    sprite: { width: 80, height: 88, variant: "piano_upright" },
    material: "wood",
  },

  {
    id: "speaker_shelf",
    name: "Shelf Speaker",
    category: "music",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "toggle",
    moodContribution: { warmth: 0.05, coziness: 0.05, energy: 0.2, mystery: 0, nature: 0 },
    soundEmission: {
      type: "music_ambient", radius: 5, volume: 0.4,
      toggleOnSound: "sfx_speaker_pop", toggleOffSound: "sfx_speaker_pop",
      hasChannels: true,
      channels: ["Ambient", "Nature Sounds", "Rain & Thunder", "White Noise", "Meditation"],
    },
    rarity: "common",
    description: "Small but surprisingly loud. Fills the room easily.",
    sprite: { width: 32, height: 36, variant: "speaker_shelf" },
    material: "wood",
  },

  // ── Game ──────────────────────────────────────────────────────────────────

  {
    id: "arcade_cabinet",
    name: "Arcade Cabinet",
    category: "game",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "arcade",
    moodContribution: { warmth: 0.05, coziness: 0, energy: 0.4, mystery: 0.05, nature: 0 },
    lightEmission: { color: "#ff40a0", radius: 2, intensity: 0.4 },
    soundEmission: { type: "sfx_arcade_idle", radius: 3, volume: 0.3, interactSound: "sfx_arcade_coin" },
    rarity: "uncommon",
    description: "Blinky and bright. Pulls you in without warning.",
    sprite: { width: 40, height: 72, variant: "arcade_cabinet" },
    material: "metal",
  },

  {
    id: "chess_table",
    name: "Chess Table",
    category: "game",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: true, interactable: true,
    interactionType: "chess",
    moodContribution: { warmth: 0.05, coziness: 0.1, energy: 0.1, mystery: 0.2, nature: 0 },
    rarity: "uncommon",
    description: "A game of quiet war. Two chairs needed.",
    sprite: { width: 44, height: 36, variant: "chess_table" },
    material: "wood",
  },

  // ── Kitchen ───────────────────────────────────────────────────────────────

  {
    id: "cooking_stove",
    name: "Cooking Stove",
    category: "kitchen",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: true, interactable: true,
    interactionType: "cooking",
    moodContribution: { warmth: 0.2, coziness: 0.15, energy: 0.1, mystery: 0, nature: 0.05 },
    soundEmission: { type: "sfx_cooking_sizzle", radius: 3, volume: 0.25, toggleOnSound: "sfx_gas_ignite", toggleOffSound: "sfx_gas_click" },
    rarity: "common",
    description: "Iron burners. The room fills with smell when it's on.",
    sprite: { width: 44, height: 44, variant: "cooking_stove" },
    material: "metal",
  },

  {
    id: "kitchen_counter",
    name: "Kitchen Counter",
    category: "kitchen",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: true, interactable: false,
    moodContribution: { warmth: 0.05, coziness: 0.05, energy: 0.05, mystery: 0, nature: 0 },
    rarity: "common",
    description: "Smooth countertop. Good for everything.",
    sprite: { width: 44, height: 40, variant: "kitchen_counter" },
    material: "stone",
  },

  // ── Outdoor ───────────────────────────────────────────────────────────────

  {
    id: "tree_potted",
    name: "Potted Tree",
    category: "outdoor",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "water",
    moodContribution: { warmth: 0.05, coziness: 0.15, energy: 0.05, mystery: 0.1, nature: 0.45 },
    rarity: "uncommon",
    description: "A small tree, somehow thriving indoors.",
    sprite: { width: 40, height: 68, variant: "tree_potted" },
    material: "organic",
  },

  {
    id: "garden_fountain",
    name: "Garden Fountain",
    category: "outdoor",
    tileWidth: 2, tileHeight: 2,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "observe",
    moodContribution: { warmth: 0, coziness: 0.2, energy: 0.1, mystery: 0.15, nature: 0.4 },
    soundEmission: { type: "sfx_water_flow", radius: 5, volume: 0.3, placeSound: "sfx_stone_thud" },
    rarity: "rare",
    description: "Water, always moving. Centering.",
    sprite: { width: 96, height: 72, variant: "garden_fountain" },
    material: "stone",
  },

  // ── Special ───────────────────────────────────────────────────────────────

  {
    id: "crystal_ball",
    name: "Crystal Ball",
    category: "special",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "divine",
    moodContribution: { warmth: 0, coziness: 0, energy: 0, mystery: 0.45, nature: 0.05 },
    lightEmission: { color: "#d0a0ff", radius: 2.5, intensity: 0.45 },
    rarity: "rare",
    description: "Violet light roils inside. You can't stop looking.",
    sprite: { width: 32, height: 40, variant: "crystal_ball" },
    material: "crystal",
  },

  {
    id: "hearth_lantern",
    name: "Hearth Lantern",
    category: "special",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "toggle",
    moodContribution: { warmth: 0.25, coziness: 0.3, energy: -0.05, mystery: 0.2, nature: 0 },
    lightEmission: { color: "#ffaa44", radius: 3.5, intensity: 0.75 },
    rarity: "seasonal",
    description: "Forged once a year. Only given, never sold.",
    sprite: { width: 32, height: 48, variant: "hearth_lantern" },
    material: "metal",
  },

  // ── Pipeline-generated ─────────────────────────────────────────────────

  {
    id: "box_animated",
    name: "Animated Box",
    category: "decoration",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: true, interactable: true,
    interactionType: "observe",
    moodContribution: { warmth: 0.05, coziness: 0.05, energy: 0.1, mystery: 0.15, nature: 0 },
    rarity: "uncommon",
    description: "A curious box that never quite stays still.",
    sprite: { width: 48, height: 64, variant: "box_animated" },
    material: "wood",
    pipelineGenerated: true,
  },

  {
    id: "leather_couch",
    name: "Leather Couch",
    category: "seating",
    tileWidth: 2, tileHeight: 1,
    canSitOn: true, canPlaceOn: false, interactable: true,
    interactionType: "sit",
    moodContribution: { warmth: 0.2, coziness: 0.4, energy: -0.1, mystery: 0, nature: 0 },
    rarity: "uncommon",
    description: "Rich leather, worn just right. Pipeline-generated from 3D model.",
    sprite: { width: 96, height: 64, variant: "leather_couch" },
    material: "fabric",
    pipelineGenerated: true,
  },

  {
    id: "macbook",
    name: "Laptop",
    category: "decoration",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: true, interactable: true,
    interactionType: "work",
    moodContribution: { warmth: 0, coziness: 0.05, energy: 0.15, mystery: 0, nature: 0 },
    rarity: "uncommon",
    description: "A sleek laptop, screen aglow. Pipeline-generated from 3D model.",
    sprite: { width: 32, height: 32, variant: "macbook" },
    material: "metal",
    pipelineGenerated: true,
  },

  {
    id: "spaceship",
    name: "Model Spaceship",
    category: "decoration",
    tileWidth: 2, tileHeight: 2,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "observe",
    moodContribution: { warmth: 0, coziness: 0.05, energy: 0.2, mystery: 0.3, nature: 0 },
    rarity: "rare",
    description: "A detailed starship model. Pipeline-generated from 3D model.",
    sprite: { width: 96, height: 80, variant: "spaceship" },
    material: "metal",
    pipelineGenerated: true,
  },

  {
    id: "audi_rs5",
    name: "Sports Car",
    category: "decoration",
    tileWidth: 3, tileHeight: 2,
    canSitOn: false, canPlaceOn: false, interactable: true,
    interactionType: "observe",
    moodContribution: { warmth: 0.05, coziness: 0, energy: 0.35, mystery: 0.1, nature: 0 },
    rarity: "legendary",
    description: "A gleaming sports car. Pipeline-generated from 3D model.",
    sprite: { width: 128, height: 72, variant: "audi_rs5" },
    material: "metal",
    pipelineGenerated: true,
  },

  {
    id: "buster_drone",
    name: "Drone",
    category: "decoration",
    tileWidth: 1, tileHeight: 1,
    canSitOn: false, canPlaceOn: true, interactable: true,
    interactionType: "observe",
    moodContribution: { warmth: 0, coziness: 0, energy: 0.25, mystery: 0.15, nature: 0 },
    rarity: "rare",
    description: "A compact aerial drone. Pipeline-generated from 3D model.",
    sprite: { width: 40, height: 40, variant: "buster_drone" },
    material: "metal",
    pipelineGenerated: true,
  },
];

// ─── Registry Access ──────────────────────────────────────────────────────────

/** Indexed map for O(1) lookup by ID */
const FURNITURE_MAP = new Map<string, FurnitureType>(
  FURNITURE_TYPES.map((f) => [f.id, f])
);

/** Look up a furniture type by ID. Returns undefined if not found. */
export function getFurnitureType(id: string): FurnitureType | undefined {
  return FURNITURE_MAP.get(id);
}

/** All registered furniture types as an array. */
export function getAllFurnitureTypes(): readonly FurnitureType[] {
  return FURNITURE_TYPES;
}

/** Filter furniture by category. */
export function getFurnitureByCategory(category: FurnitureCategory): FurnitureType[] {
  return FURNITURE_TYPES.filter((f) => f.category === category);
}

/** Filter furniture by rarity. */
export function getFurnitureByRarity(
  rarity: FurnitureType["rarity"]
): FurnitureType[] {
  return FURNITURE_TYPES.filter((f) => f.rarity === rarity);
}

// ─── Isometric Dimensions & Wall Snapping ─────────────────────────────────────
//
// Each furniture variant's base [width, depth] in isometric pixels. Used to
// compute exact wall-flush positioning so the back face of each item sits
// tight against the wall with no gap.
//
// Width = extent along iso-east axis (screen right+up, decreases wy)
// Depth = extent along iso-west axis (screen left+up, decreases wx)

// ── Canonical Iso Dimensions ──────────────────────────────────────────────────
//
// [w, d, canonicalDir]:
//   w, d          — rendered iso dimensions at the canonical direction (in iso pixels)
//   canonicalDir  — the semantic Direction at which visualRot evaluates to 0
//
// When placing at direction D, the VISUAL rotation passed to isoRotate is:
//   visualRot = (toRotation(D) - toRotation(canonicalDir) + 4) % 4
//
// At visualRot 0 or 2 (flip=false): renderer gets [w, d] — designed proportions.
// At visualRot 1 or 3 (flip=true):  renderer gets [d, w] — 90°-rotated proportions.
// Footprint, wall-snap, and rendering all use visual rotation — not raw direction.

export const FURNITURE_ISO_DIMS: Record<string, [number, number, canonDir: Direction]> = {
  sofa_oak:      [58, 22, "S"],   // designed long-axis=w; seat faces camera at dir "S"
  chair_wooden:  [18, 18, "SE"],  // symmetric
  bed_cozy:      [26, 54, "NW"],  // dir "N" → visualRot 1 (flip, !back): headboard against wall, extends south
  bookshelf:     [28, 14, "SE"],  // wide back panel facing east
  desk_study:    [28, 20, "SE"],  // kneehole faces east
  coffee_table:  [24, 16, "SE"],
  small_table:   [20, 14, "SE"],
  floor_lamp:    [12, 12, "SE"],  // symmetric
  fireplace:     [28, 14, "S"],   // flames face camera at dir "S"
  potted_plant:  [12, 12, "SE"],  // symmetric
  rug_woven:     [48, 48, "SE"],  // symmetric
  aeterna_device:[12, 4,  "SE"],
  chest_old:     [22, 14, "SE"],
  candle:        [8,  8,  "SE"],  // symmetric
  chandelier:    [16, 16, "SE"],  // symmetric
  computer_desk: [18, 6,  "SE"],
  hammock:       [56, 18, "SE"],
  window_frame:  [26, 4,  "SE"],
  fish_tank:     [22, 14, "SE"],
  wall_art:      [22, 3,  "SE"],
  record_player: [22, 16, "SE"],
  piano_upright: [50, 18, "SE"],
  speaker_shelf: [14, 12, "SE"],
  arcade_cabinet:[18, 14, "SE"],
  chess_table:   [22, 22, "SE"],  // symmetric
  cooking_stove: [24, 20, "SE"],
  kitchen_counter:[24, 18, "SE"],
  tree_potted:   [16, 16, "SE"],  // symmetric
  garden_fountain:[48, 48, "SE"], // symmetric
  crystal_ball:  [14, 14, "SE"],  // symmetric
  hearth_lantern:[12, 12, "SE"],  // symmetric
};

/** Convert a direction string to a 0-3 rotation index (matches render convention). */
function toRotation(dir: string): 0 | 1 | 2 | 3 {
  const d = dir.toUpperCase();
  if (d === "E"  || d === "SE") return 0;
  if (d === "S"  || d === "SW") return 1;
  if (d === "W"  || d === "NW") return 2;
  return 3;
}

/** Default wall-snap for items without iso dimension data (assumes ~16px depth). */
const WALL_SNAP = 0.5;

/** Resolve a placement type to a FurnitureType, handling starter room aliases. */
function resolveFurnitureType(typeId: string): FurnitureType | undefined {
  return getFurnitureType(typeId) ?? getFurnitureType(
    ({ bed: "bed_cozy", desk: "desk_study", aeterna: "aeterna_device",
       desk_chair: "chair_wooden", rug_round: "rug_woven",
       potted_fern: "potted_plant", sofa: "sofa_oak",
       floor_lamp: "floor_lamp", bookshelf: "bookshelf",
       fireplace: "fireplace", chair: "chair_wooden" } as Record<string, string>)[typeId] ?? typeId
  );
}

/**
 * Compute the RENDER anchor world position for a furniture placement.
 *
 * isoBox primitives extend backward (northwest) from their south corner:
 *   depth (d) extends along WEST axis → decreasing wx
 *   width (w) extends along EAST axis → decreasing wy
 *
 * For multi-tile furniture, the south corner must be at the SE-most point
 * of the full footprint so the rendered box covers the correct tiles:
 *   wx = tileX + footprintW - 0.5
 *   wy = tileY + footprintH - 0.5
 * For 1x1 items this reduces to the original (tileX+0.5, tileY+0.5).
 *
 * Wall-snap closes any gap between the back face and adjacent walls.
 */

/** Canonical-adjusted visual rotation for rendering. */
export function getVisualRotation(typeId: string, placementDir: string): 0 | 1 | 2 | 3 {
  const ft = resolveFurnitureType(typeId);
  const rot = toRotation(placementDir);
  if (!ft) return rot;
  const variant = ft.sprite.variant;
  const dims = FURNITURE_ISO_DIMS[variant];
  if (!dims) return rot;
  const canon = toRotation(dims[2]);
  return ((rot - canon + 4) % 4) as 0 | 1 | 2 | 3;
}

export function furnitureRenderPosition(
  placement: FurniturePlacement,
  walls: WallSegment[],
): { wx: number; wy: number } {
  const ft = resolveFurnitureType(placement.type);

  const vRot = getVisualRotation(placement.type, placement.direction);
  const flip = vRot === 1 || vRot === 3;
  const tw = ft ? ft.tileWidth : 1;
  const th = ft ? ft.tileHeight : 1;
  // When rotated 90/270°, tile footprint swaps
  const footW = flip ? th : tw;  // tiles along X (depth iso axis)
  const footH = flip ? tw : th;  // tiles along Y (width iso axis)

  let wx = placement.tileX + footW - 0.5;
  let wy = placement.tileY + footH - 0.5;

  // ── Wall-snap ───────────────────────────────────────────────────────────
  let hasNorth = false;
  let hasWest  = false;
  for (const w of walls) {
    if (w.side === "N" && w.x === placement.tileX && w.y === placement.tileY) hasNorth = true;
    if (w.side === "W" && w.x === placement.tileX && w.y === placement.tileY) hasWest  = true;
    if (hasNorth && hasWest) break;
  }

  if (!hasNorth && !hasWest) return { wx, wy };

  const variant = ft?.sprite.variant;
  const dims = variant ? FURNITURE_ISO_DIMS[variant] : undefined;

  if (dims) {
    const [baseW, baseD] = dims;
    const renderedW = flip ? baseD : baseW;
    const renderedD = flip ? baseW : baseD;

    // Snap toward wall: push the back face to coordinate -0.5 (wall surface).
    // Anchor is at (tileX+footW-0.5, tileY+footH-0.5). The rendered box
    // extends renderedW/32 tiles backward along wy. For the back face at -0.5:
    //   (footH - 0.5) - snap - renderedW/32 = -0.5
    //   snap = footH - renderedW/32
    // Works for 1×1 AND multi-tile, any direction. Furniture at a wall always
    // snaps flush — the formula naturally handles any orientation because it
    // uses the actual rendered extent toward the wall, not a direction check.
    if (hasNorth) {
      const snapN = Math.max(0, footH - renderedW / 32);
      wy -= snapN;
    }
    if (hasWest) {
      const snapW = Math.max(0, footW - renderedD / 32);
      wx -= snapW;
    }
  } else {
    if (hasNorth) wy -= WALL_SNAP;
    if (hasWest)  wx -= WALL_SNAP;
  }

  return { wx, wy };
}

/**
 * Compute wall-snapped world position for player sitting/sleeping on furniture.
 *
 * Starts from the footprint center (tileX + footW/2, tileY + footH/2) and
 * applies half the wall-snap offset so the player ends up centered on the
 * visible furniture, not at the render anchor or at the wall edge.
 */
export function wallSnappedPosition(
  placement: FurniturePlacement,
  walls: WallSegment[],
): { wx: number; wy: number } {
  const ft = resolveFurnitureType(placement.type);
  const vRot = getVisualRotation(placement.type, placement.direction);
  const flip = vRot === 1 || vRot === 3;
  const tw = ft ? ft.tileWidth : 1;
  const th = ft ? ft.tileHeight : 1;
  const footW = flip ? th : tw;
  const footH = flip ? tw : th;

  // Start at the center of the tile footprint
  let wx = placement.tileX + footW / 2;
  let wy = placement.tileY + footH / 2;

  // Compute wall-snap (same logic as furnitureRenderPosition) and apply half
  let hasNorth = false;
  let hasWest  = false;
  for (const w of walls) {
    if (w.side === "N" && w.x === placement.tileX && w.y === placement.tileY) hasNorth = true;
    if (w.side === "W" && w.x === placement.tileX && w.y === placement.tileY) hasWest  = true;
    if (hasNorth && hasWest) break;
  }

  const variant = ft?.sprite.variant;
  const dims = variant ? FURNITURE_ISO_DIMS[variant] : undefined;

  if (dims) {
    const [baseW, baseD] = dims;
    const renderedW = flip ? baseD : baseW;
    const renderedD = flip ? baseW : baseD;

    if (hasNorth) {
      const snapN = Math.max(0, footH - renderedW / 32);
      wy -= snapN * 0.5;
    }
    if (hasWest) {
      const snapW = Math.max(0, footW - renderedD / 32);
      wx -= snapW * 0.5;
    }
  } else {
    if (hasNorth) wy -= WALL_SNAP * 0.5;
    if (hasWest)  wx -= WALL_SNAP * 0.5;
  }

  return { wx, wy };
}

/**
 * Get all tile coordinates occupied by a furniture placement's footprint.
 *
 * tileX/tileY is the NW corner of the footprint. Rotation (flip) swaps
 * tileWidth and tileHeight to determine the actual extent.
 *
 * ── Footprint convention ────────────────────────────────────────────────
 * For a 2×1 sofa facing S (rotation 1, flip=true):
 *   tileWidth=2, tileHeight=1 → footW=1, footH=2
 *   Tiles: (tileX, tileY), (tileX, tileY+1)
 *
 * For a 2×2 rug (no rotation matters):
 *   Tiles: (tileX, tileY) through (tileX+1, tileY+1)
 * ─────────────────────────────────────────────────────────────────────────
 */
export function getFootprintTiles(
  placement: { type: string; tileX: number; tileY: number; direction: string },
): Array<{ x: number; y: number }> {
  const ft = resolveFurnitureType(placement.type);
  const vRot = getVisualRotation(placement.type, placement.direction);
  const flip = vRot === 1 || vRot === 3;
  const tw = ft ? ft.tileWidth : 1;
  const th = ft ? ft.tileHeight : 1;
  const footW = flip ? th : tw;
  const footH = flip ? tw : th;

  const tiles: Array<{ x: number; y: number }> = [];
  for (let dx = 0; dx < footW; dx++) {
    for (let dy = 0; dy < footH; dy++) {
      tiles.push({ x: placement.tileX + dx, y: placement.tileY + dy });
    }
  }
  return tiles;
}

// ─── Procedural Rendering ─────────────────────────────────────────────────────

/**
 * Draw a furniture sprite procedurally onto a canvas context.
 *
 * All sprites use fillRect exclusively — no images, no SVG, no external assets.
 * The aesthetic is deliberate: soft, chunky pixel art in a warm Habbo-adjacent
 * palette. Less is more. Silhouette first, detail second.
 *
 * @param ctx        Canvas 2D rendering context
 * @param furniture  The furniture type definition
 * @param screenX    Canvas X for the sprite's bottom-left corner (isometric pivot)
 * @param screenY    Canvas Y for the sprite's bottom corner
 * @param direction  0-3 rotation states (0=SE, 1=SW, 2=NW, 3=NE)
 * @param state      Per-instance interactive state (on/off, animation frame, etc.)
 * @param time       Current timestamp (ms) for animated sprites
 */
export function renderFurniture(
  ctx: CanvasRenderingContext2D,
  furniture: FurnitureType,
  screenX: number,
  screenY: number,
  direction: 0 | 1 | 2 | 3,
  state: Record<string, unknown>,
  time: number = 0
): void {
  const v = furniture.sprite.variant;
  const on = state.on !== false; // default to on

  ctx.save();

  // screenX/screenY is the tile CENTER in screen space (from worldToScreen at
  // tileX+0.5, tileY+0.5). The isoBox primitives anchor at the SOUTH corner of
  // the tile diamond, which is TILE_H/2 = 16px below tile center.
  const sx = screenX;
  const sy = screenY + 16;

  // ─── isoBoxAt Offset Convention ────────────────────────────────────────
  //
  // To place a sub-component offset from the south corner (sx, sy):
  //
  //   Pure east by E px:  ox = E,      oy = -E * 0.5
  //   Pure west by M px:  ox = -M,     oy = -M * 0.5
  //   Combined (E + M):   ox = E - M,  oy = -(E + M) * 0.5
  //
  // Both iso axes slope upward on screen, so oy is always negative for
  // positive displacement. Height lifts are an additional negative oy term:
  //   oy = -(E + M) * 0.5 - heightOffset
  //
  // For complex furniture, use a local helper:
  //   const off = (e, m) => ({ ox: e - m, oy: -(e + m) * 0.5 });
  // ──────────────────────────────────────────────────────────────────────

  switch (v) {
    // ── Sofa ───────────────────────────────────────────────────────────────
    case "sofa_oak": {
      // 2-seat sofa. Base dims [22, 58].
      // w = extent along EAST axis (right on screen), d = extent along WEST axis (left).
      // back=true means the sofa's backrest faces the CAMERA (open/seat side faces away).
      const { w, d, back } = isoRotate(sx, sy, 22, 58, direction);
      const off = (e: number, m: number) => ({ ox: e - m, oy: -(e + m) * 0.5 });
      const armW = 6;    // armrest width along east (sofa length)
      const backD = 5;   // back depth along west (depth axis)

      // ── Draw order: back-to-front. When back=true, the backrest is NEAR camera. ──

      if (!back) {
        // NORMAL: backrest at far west, seat faces camera

        // Back rest — at far end of depth axis
        const bk = off(0, d - backD);
        isoBoxAt(ctx, sx, sy, bk.ox, bk.oy, w, backD, 20, "#8B6F5E", "#7A5E4E", "#9B7F6E");
        // Back cushion
        const bc = off(0, d - backD + 1);
        isoBoxAt(ctx, sx, sy, bc.ox, bc.oy, w - armW * 2, 3, 18, "#D4A090", "#C4907A", "#DEB09A");

        // Far armrest
        const fa = off(w - armW, 0);
        isoBoxAt(ctx, sx, sy, fa.ox, fa.oy, armW, d, 14, "#7A5828", "#6A4818", "#8A6838");
        isoBoxAt(ctx, sx, sy, fa.ox, fa.oy, armW, d, 15, "#C89878", "#B88868", "#D8A888");

        // Wooden base frame
        isoBox(ctx, sx, sy, w, d, 5, "#8B6035", "#7A5025", "#9B7045");

        // Seat cushions
        const seatD = d - backD;
        const seatW = w - armW * 2;
        const cushW = Math.round((seatW - 1) / 2);
        const c1 = off(armW, 0);
        isoBoxAt(ctx, sx, sy, c1.ox, c1.oy, cushW, seatD, 11, "#DEB09A", "#CDA08A", "#EEC0AA");
        const c2 = off(armW + cushW + 1, 0);
        isoBoxAt(ctx, sx, sy, c2.ox, c2.oy, cushW, seatD, 11, "#D4A690", "#C49680", "#E4B6A0");
        const gp = off(armW + cushW, 0);
        ctx.globalAlpha = 0.08;
        isoBoxAt(ctx, sx, sy, gp.ox, gp.oy, 1, seatD, 11, "#000", "#000", "#000");
        ctx.globalAlpha = 1;

        // Near armrest (closest to camera, drawn last)
        isoBox(ctx, sx, sy, armW, d, 14, "#7A5828", "#6A4818", "#8A6838");
        isoBox(ctx, sx, sy, armW, d, 15, "#C89878", "#B88868", "#D8A888");
      } else {
        // BACK: backrest faces camera (near south corner), seat faces away

        // Far armrest — drawn first (furthest from camera)
        const fa = off(w - armW, 0);
        isoBoxAt(ctx, sx, sy, fa.ox, fa.oy, armW, d, 14, "#7A5828", "#6A4818", "#8A6838");
        isoBoxAt(ctx, sx, sy, fa.ox, fa.oy, armW, d, 15, "#C89878", "#B88868", "#D8A888");

        // Seat cushions — at the FAR end of depth (away from camera)
        const seatD = d - backD;
        const seatW = w - armW * 2;
        const cushW = Math.round((seatW - 1) / 2);
        const c1 = off(armW, d - seatD);
        isoBoxAt(ctx, sx, sy, c1.ox, c1.oy, cushW, seatD, 11, "#DEB09A", "#CDA08A", "#EEC0AA");
        const c2 = off(armW + cushW + 1, d - seatD);
        isoBoxAt(ctx, sx, sy, c2.ox, c2.oy, cushW, seatD, 11, "#D4A690", "#C49680", "#E4B6A0");
        const gp = off(armW + cushW, d - seatD);
        ctx.globalAlpha = 0.08;
        isoBoxAt(ctx, sx, sy, gp.ox, gp.oy, 1, seatD, 11, "#000", "#000", "#000");
        ctx.globalAlpha = 1;

        // Wooden base frame
        isoBox(ctx, sx, sy, w, d, 5, "#8B6035", "#7A5025", "#9B7045");

        // Back cushion — near camera, in front of backrest
        const bc = off(0, backD - 1);
        isoBoxAt(ctx, sx, sy, bc.ox, bc.oy, w - armW * 2, 3, 18, "#D4A090", "#C4907A", "#DEB09A");

        // Backrest — at NEAR end (south corner), drawn late = in front
        isoBox(ctx, sx, sy, w, backD, 20, "#8B6F5E", "#7A5E4E", "#9B7F6E");

        // Near armrest (closest to camera, drawn last)
        isoBox(ctx, sx, sy, armW, d, 14, "#7A5828", "#6A4818", "#8A6838");
        isoBox(ctx, sx, sy, armW, d, 15, "#C89878", "#B88868", "#D8A888");
      }
      break;
    }

    // ── Wooden Chair ───────────────────────────────────────────────────────
    case "chair_wooden": {
      const { w, d, back } = isoRotate(sx, sy, 18, 18, direction);
      const off = (e: number, m: number) => ({ ox: e - m, oy: -(e + m) * 0.5 });
      const legS = 2;
      const seatH = 10;   // height where seat sits (front leg height)
      const backH = 28;   // back leg/upright total height

      if (!back) {
        // NORMAL: backrest at far west (away from camera), seat faces camera
        // Back legs (tall uprights) — drawn first (furthest)
        const bl = off(0, d - legS);
        isoBoxAt(ctx, sx, sy, bl.ox, bl.oy, legS, legS, backH, "#8B6035", "#7B5025", "#9B7045");
        const br = off(w - legS, d - legS);
        isoBoxAt(ctx, sx, sy, br.ox, br.oy, legS, legS, backH, "#8B6035", "#7B5025", "#9B7045");
        // Backrest rails
        isoBoxAt(ctx, sx, sy, bl.ox, bl.oy - backH, w, 2, 3, "#9B7045", "#8B6035", "#AB8055");
        isoBoxAt(ctx, sx, sy, bl.ox, bl.oy - 19, w, 1.5, 2, "#8B6035", "#7B5025", "#9B7045");
        // Seat — elevated thin slab
        isoBoxAt(ctx, sx, sy, 0, -seatH, w, d, 3, "#B08848", "#A07838", "#C09858");
        // Front legs — drawn last (closest to camera)
        isoBox(ctx, sx, sy, legS, legS, seatH, "#7A5528", "#6A4518", "#8A6538");
        const fr = off(w - legS, 0);
        isoBoxAt(ctx, sx, sy, fr.ox, fr.oy, legS, legS, seatH, "#7A5528", "#6A4518", "#8A6538");
      } else {
        // BACK: backrest faces camera (near south corner), open seat faces away
        // Far legs (short, at back/far end) — drawn first
        const fl = off(0, d - legS);
        isoBoxAt(ctx, sx, sy, fl.ox, fl.oy, legS, legS, seatH, "#7A5528", "#6A4518", "#8A6538");
        const fr = off(w - legS, d - legS);
        isoBoxAt(ctx, sx, sy, fr.ox, fr.oy, legS, legS, seatH, "#7A5528", "#6A4518", "#8A6538");
        // Seat — elevated thin slab
        isoBoxAt(ctx, sx, sy, 0, -seatH, w, d, 3, "#B08848", "#A07838", "#C09858");
        // Near legs (tall uprights with backrest) — drawn last (closest)
        isoBox(ctx, sx, sy, legS, legS, backH, "#8B6035", "#7B5025", "#9B7045");
        const nr = off(w - legS, 0);
        isoBoxAt(ctx, sx, sy, nr.ox, nr.oy, legS, legS, backH, "#8B6035", "#7B5025", "#9B7045");
        // Backrest rails — at near south corner, on top of near legs
        isoBoxAt(ctx, sx, sy, 0, -backH, w, 2, 3, "#9B7045", "#8B6035", "#AB8055");
        isoBoxAt(ctx, sx, sy, 0, -19, w, 1.5, 2, "#8B6035", "#7B5025", "#9B7045");
      }
      break;
    }

    // ── Bed ────────────────────────────────────────────────────────────────
    case "bed_cozy": {
      // Base dims [26, 54]: w=26 (depth, front-to-back), d=54 (length, along wall).
      // back=true: headboard faces camera, footboard faces away.
      const { w, d, back } = isoRotate(sx, sy, 26, 54, direction);
      const off = (e: number, m: number) => ({ ox: e - m, oy: -(e + m) * 0.5 });
      const pillowW = Math.round((w - 4) / 2);
      const pillowD = Math.max(4, Math.round(d * 0.16));

      if (!back) {
        // NORMAL: headboard at far west, footboard at south corner

        // Headboard — far west, drawn first
        const hb = off(0, d - 2);
        isoBoxAt(ctx, sx, sy, hb.ox, hb.oy, w + 1, 4, 26, "#6B4F38", "#5B3F28", "#7B5F48");
        const hc = off(0, d - 1);
        isoBoxAt(ctx, sx, sy, hc.ox, hc.oy, w + 2, 3, 28, "#7B5F48", "#6B4F38", "#8B6F58");

        // Frame
        isoBox(ctx, sx, sy, w, d, 8, "#7A5A40", "#6A4A30", "#8A6A50");

        // Mattress
        const mt = off(1, 1);
        isoBoxAt(ctx, sx, sy, mt.ox, mt.oy, w - 2, d - 3, 14, "#EDE0CC", "#DDD0BC", "#F5E8D4");
        isoBoxAt(ctx, sx, sy, mt.ox, mt.oy, w - 2, d - 3, 15, "#F0E6D8", "#E0D6C8", "#F8EEE0");

        // Pillows — at head (far west)
        const pw = d - pillowD - 3;
        const p1 = off(1, pw);
        isoBoxAt(ctx, sx, sy, p1.ox, p1.oy, pillowW, pillowD, 18, "#F8F0E4", "#E8E0D4", "#FFFFF4");
        const p2 = off(1 + pillowW + 2, pw);
        isoBoxAt(ctx, sx, sy, p2.ox, p2.oy, pillowW, pillowD, 18, "#F4ECE0", "#E4DCD0", "#FFF8EE");

        // Blanket — front ~50%
        const blanketD = Math.round(d * 0.5);
        isoBox(ctx, sx, sy, w, blanketD, 17, "#9B7050", "#8B6040", "#AB8060");
        const fold = off(0, blanketD);
        isoBoxAt(ctx, sx, sy, fold.ox, fold.oy, w, 2, 18, "#A87858", "#987048", "#B88868");

        // Footboard — south corner
        isoBox(ctx, sx, sy, w, 3, 14, "#6B4F38", "#5B3F28", "#7B5F48");
      } else {
        // BACK: headboard faces camera (near south), footboard at far west

        // Footboard — far west, drawn first
        const fb = off(0, d - 3);
        isoBoxAt(ctx, sx, sy, fb.ox, fb.oy, w, 3, 14, "#6B4F38", "#5B3F28", "#7B5F48");

        // Frame
        isoBox(ctx, sx, sy, w, d, 8, "#7A5A40", "#6A4A30", "#8A6A50");

        // Mattress
        const mt = off(1, 1);
        isoBoxAt(ctx, sx, sy, mt.ox, mt.oy, w - 2, d - 3, 14, "#EDE0CC", "#DDD0BC", "#F5E8D4");
        isoBoxAt(ctx, sx, sy, mt.ox, mt.oy, w - 2, d - 3, 15, "#F0E6D8", "#E0D6C8", "#F8EEE0");

        // Blanket — at the FAR end (away from camera, beyond pillows)
        const blanketD = Math.round(d * 0.5);
        const bOff = off(0, d - blanketD);
        isoBoxAt(ctx, sx, sy, bOff.ox, bOff.oy, w, blanketD, 17, "#9B7050", "#8B6040", "#AB8060");
        const fold = off(0, d - blanketD);
        isoBoxAt(ctx, sx, sy, fold.ox, fold.oy, w, 2, 18, "#A87858", "#987048", "#B88868");

        // Pillows — near camera (small offset from south corner)
        const pw = 3;  // small inset from south edge
        const p1 = off(1, pw);
        isoBoxAt(ctx, sx, sy, p1.ox, p1.oy, pillowW, pillowD, 18, "#F8F0E4", "#E8E0D4", "#FFFFF4");
        const p2 = off(1 + pillowW + 2, pw);
        isoBoxAt(ctx, sx, sy, p2.ox, p2.oy, pillowW, pillowD, 18, "#F4ECE0", "#E4DCD0", "#FFF8EE");

        // Headboard — at south corner, drawn last (in front)
        isoBox(ctx, sx, sy, w + 1, 4, 26, "#6B4F38", "#5B3F28", "#7B5F48");
        isoBox(ctx, sx, sy, w + 2, 3, 28, "#7B5F48", "#6B4F38", "#8B6F58");
      }
      break;
    }

    // ── Hammock ────────────────────────────────────────────────────────────
    case "hammock": {
      const { w, d } = isoRotate(sx, sy, 56, 18, direction);
      // Left pole — thick wooden post
      isoBox(ctx, sx, sy, 4, 4, 32, "#8B6035", "#7A5025", "#9B7045");
      isoBox(ctx, sx, sy, 4, 4, 34, "#7A5528", "#6A4518", "#8A6538"); // cap
      // Right pole
      isoBoxAt(ctx, sx, sy, w - 4, -(w - 4) * 0.5, 4, 4, 32, "#8B6035", "#7A5025", "#9B7045");
      isoBoxAt(ctx, sx, sy, w - 4, -(w - 4) * 0.5, 4, 4, 34, "#7A5528", "#6A4518", "#8A6538");
      // Rope attachments (top of each pole)
      isoBoxAt(ctx, sx, sy, 4, -4 * 0.5 - 28, 3, 2, 2, "#C8A878", "#B89868", "#D8B888");
      isoBoxAt(ctx, sx, sy, w - 7, -(w - 7) * 0.5 - 28, 3, 2, 2, "#C8A878", "#B89868", "#D8B888");
      // Fabric body — slung between poles, sagging in the middle
      // Lower section (sagging center)
      isoBoxAt(ctx, sx, sy, 8, -8 * 0.5 - 14, w - 16, d, 3, "#D4B896", "#C4A886", "#E4C8A6");
      // Upper edges (where rope meets fabric, higher)
      isoBoxAt(ctx, sx, sy, 5, -5 * 0.5 - 20, 10, d * 0.8, 2, "#CCAE8A", "#BCA07A", "#DCBE9A");
      isoBoxAt(ctx, sx, sy, w - 15, -(w - 15) * 0.5 - 20, 10, d * 0.8, 2, "#CCAE8A", "#BCA07A", "#DCBE9A");
      // Pillow in hammock
      isoBoxAt(ctx, sx, sy, 10, -10 * 0.5 - 16, 8, d * 0.6, 4, "#F0E6D8", "#E0D6C8", "#F8EEE0");
      break;
    }

    // ── Coffee Table ───────────────────────────────────────────────────────
    case "coffee_table": {
      const { w, d } = isoRotate(sx, sy, 24, 16, direction);
      // Four tapered legs
      const cLeg = 2;
      isoBox(ctx, sx, sy, cLeg, cLeg, 8, "#7A5828", "#6A4818", "#8A6838");
      isoBoxAt(ctx, sx, sy, w - cLeg, -(w - cLeg) * 0.5, cLeg, cLeg, 8, "#7A5828", "#6A4818", "#8A6838");
      isoBoxAt(ctx, sx, sy, -d + cLeg, -(d - cLeg) * 0.5, cLeg, cLeg, 8, "#7A5828", "#6A4818", "#8A6838");
      isoBoxAt(ctx, sx, sy, w - cLeg - d + cLeg, -(w - cLeg) * 0.5 - (d - cLeg) * 0.5, cLeg, cLeg, 8, "#7A5828", "#6A4818", "#8A6838");
      // Lower shelf
      isoBoxAt(ctx, sx, sy, 1, -1 * 0.5 - 3, w - 2, d - 2, 1.5, "#9A7040", "#8A6030", "#AA8050");
      // Tabletop — warm polished wood
      isoBox(ctx, sx, sy, w, d, 10, "#B88850", "#A87840", "#C89860");
      isoBox(ctx, sx, sy, w, d, 11, "#C09058", "#B08048", "#D0A068");
      break;
    }

    // ── Study Desk ─────────────────────────────────────────────────────────
    case "desk_study": {
      // Study desk: back panel against wall, drawer pedestal on one side, open leg space.
      // back=true: back panel faces camera (desk viewed from behind).
      const { w, d, back } = isoRotate(sx, sy, 28, 20, direction);
      const off = (e: number, m: number) => ({ ox: e - m, oy: -(e + m) * 0.5 });
      const deskH = 16;   // desktop surface height
      const legW = 2;     // leg/panel thickness

      if (!back) {
        // NORMAL: back panel at far west, open kneehole faces camera

        // Back panel — at far depth, thin, full width
        const bp = off(0, d - legW);
        isoBoxAt(ctx, sx, sy, bp.ox, bp.oy, w, legW, deskH, "#7A5828", "#6A4818", "#8A6838");

        // Left leg panel — solid side at south-east corner
        isoBox(ctx, sx, sy, legW, d, deskH - 2, "#806030", "#704020", "#907040");

        // Right-side drawer pedestal — at far east end
        const dp = off(w * 0.55, 0);
        isoBoxAt(ctx, sx, sy, dp.ox, dp.oy, w * 0.42, d, deskH - 2, "#907038", "#806028", "#A08048");
        // Drawer faces (front-facing, on the south/camera side of pedestal)
        const df = off(w * 0.57, 0);
        isoBoxAt(ctx, sx, sy, df.ox, df.oy, w * 0.38, d * 0.1, 6, "#A08040", "#907030", "#B09050");
        isoBoxAt(ctx, sx, sy, df.ox, df.oy - 6, w * 0.38, d * 0.1, 6, "#A08040", "#907030", "#B09050");
        // Drawer handles
        const dh = off(w * 0.7, 0);
        isoBoxAt(ctx, sx, sy, dh.ox, dh.oy, 3, 1, 4, "#B09050", "#A08040", "#C0A060");
        isoBoxAt(ctx, sx, sy, dh.ox, dh.oy - 6, 3, 1, 10, "#B09050", "#A08040", "#C0A060");
      } else {
        // BACK: back panel at south corner (faces camera), kneehole hidden behind.
        // Leg/drawer are behind the back panel, mostly occluded — draw them first.

        // Leg panel — behind back panel, at far depth
        const lp = off(0, d - legW);
        isoBoxAt(ctx, sx, sy, lp.ox, lp.oy, legW, legW, deskH - 2, "#806030", "#704020", "#907040");

        // Drawer pedestal — also behind back panel
        const dp = off(w * 0.55, legW);
        isoBoxAt(ctx, sx, sy, dp.ox, dp.oy, w * 0.42, d - legW, deskH - 2, "#907038", "#806028", "#A08048");

        // Back panel — at south corner (camera-facing), full width, drawn last
        isoBox(ctx, sx, sy, w, legW, deskH, "#7A5828", "#6A4818", "#8A6838");
      }

      // Desktop surface — ELEVATED THIN SLAB so structure below remains visible
      isoBoxAt(ctx, sx, sy, 0, -deskH, w, d, 2, "#D4AC6C", "#C49C5C", "#E0B878");
      isoBoxAt(ctx, sx, sy, 0, -deskH - 1, w, d, 1, "#DCBA78", "#CCA868", "#E8C688");
      break;
    }

    // ── Small Table ────────────────────────────────────────────────────────
    case "small_table": {
      const { w, d } = isoRotate(sx, sy, 20, 14, direction);
      // Single pedestal base
      isoBox(ctx, sx, sy, w * 0.6, d * 0.6, 3, "#806030", "#704020", "#907040");
      // Central column
      isoBoxAt(ctx, sx, sy, w * 0.35, -w * 0.35 * 0.5, 5, 5, 14, "#907030", "#804020", "#A08040");
      // Tabletop
      isoBox(ctx, sx, sy, w, d, 16, "#B89050", "#A88040", "#C8A060");
      isoBox(ctx, sx, sy, w, d, 17, "#C09858", "#B08848", "#D0A868");
      break;
    }

    // ── Bookshelf ──────────────────────────────────────────────────────────
    case "bookshelf": {
      // Tall piece — back panel flush with wall. back=true: back panel faces camera (you see
      // the back of the bookshelf, not the books).
      const { w, d, back } = isoRotate(sx, sy, 28, 14, direction);
      const off = (e: number, m: number) => ({ ox: e - m, oy: -(e + m) * 0.5 });

      if (!back) {
        // NORMAL: back panel at far west, books face camera
        const bp = off(0, d - 2);
        isoBoxAt(ctx, sx, sy, bp.ox, bp.oy, w, 2, 54, "#5A4018", "#4A3008", "#6A5028");
        isoBox(ctx, sx, sy, 2, d, 54, "#7A5828", "#6A4818", "#8A6838");
        isoBoxAt(ctx, sx, sy, w - 2, -(w - 2) * 0.5, 2, d, 54, "#7A5828", "#6A4818", "#8A6838");
        isoBox(ctx, sx, sy, w, d + 1, 4, "#6A4818", "#5A3808", "#7A5828");
        const bookColors = ["#C04040","#4080C0","#40A840","#D08030","#8040B0","#C07030","#3090A0","#C03060","#B06020","#5070B0","#609040","#D04060"];
        for (let shelf = 0; shelf < 5; shelf++) {
          const shelfH = 4 + shelf * 10;
          isoBoxAt(ctx, sx, sy, 0, -shelfH, w, d, 1.5, "#8A6838", "#7A5828", "#9A7848");
          const bookCount = 4 + (shelf % 2);
          const usableW = w - 5;
          for (let b = 0; b < bookCount; b++) {
            const bw = usableW / bookCount - 0.5;
            const bOff = 2.5 + b * (usableW / bookCount);
            const bh = 6 + ((shelf * 5 + b) % 4) * 1.5;
            const bd = d * 0.55 + ((b + shelf) % 3) * 1;
            const ci = (shelf * bookCount + b) % bookColors.length;
            const c = bookColors[ci];
            isoBoxAt(ctx, sx, sy, bOff, -bOff * 0.5 - shelfH - 1.5, bw, bd, bh, c, c, c);
          }
        }
        isoBoxAt(ctx, sx, sy, -1, -54 + 0.5, w + 2, d + 2, 2.5, "#8A6838", "#7A5828", "#9A7848");
      } else {
        // BACK: back panel faces camera — you see the flat wooden back, no books
        // Side panels (drawn first — furthest)
        isoBox(ctx, sx, sy, 2, d, 54, "#7A5828", "#6A4818", "#8A6838");
        isoBoxAt(ctx, sx, sy, w - 2, -(w - 2) * 0.5, 2, d, 54, "#7A5828", "#6A4818", "#8A6838");
        // Base plinth
        isoBox(ctx, sx, sy, w, d + 1, 4, "#6A4818", "#5A3808", "#7A5828");
        // Back panel — at south corner, full width (drawn last = in front)
        isoBox(ctx, sx, sy, w, 2, 54, "#5A4018", "#4A3008", "#6A5028");
        // Top cap
        isoBoxAt(ctx, sx, sy, -1, -54 + 0.5, w + 2, d + 2, 2.5, "#8A6838", "#7A5828", "#9A7848");
      }
      break;
    }

    // ── Old Chest ──────────────────────────────────────────────────────────
    case "chest_old": {
      const { w, d, back } = isoRotate(sx, sy, 22, 14, direction);
      const off = (e: number, m: number) => ({ ox: e - m, oy: -(e + m) * 0.5 });
      // Main body
      isoBox(ctx, sx, sy, w, d, 14, "#8B6035", "#7A5025", "#9B7045");
      // Iron bands
      isoBox(ctx, sx, sy, w, d, 6, "#C0A050", "#B09040", "#D0B060");
      isoBoxAt(ctx, sx, sy, 0, -10, w, d, 1.5, "#C0A050", "#B09040", "#D0B060");
      // Lid
      if (state.open === true) {
        // Open lid — hinges at back. When back=true, hinge is at south (camera) side.
        if (!back) {
          isoBoxAt(ctx, sx, sy, -d, -d * 0.5 - 10, w, d * 0.8, 2, "#9B7045", "#8B6035", "#AB8055");
        } else {
          // Lid opens toward camera (rests against south edge)
          isoBox(ctx, sx, sy, w, d * 0.8, 2, "#9B7045", "#8B6035", "#AB8055");
        }
      } else {
        isoBoxAt(ctx, sx, sy, 0, -14, w, d, 4, "#9B7045", "#8B6035", "#AB8055");
        isoBoxAt(ctx, sx, sy, 0, -14, w, d, 2, "#C8A858", "#B89848", "#D8B868");
      }
      // Latch — on front face (camera side when normal, back side when back)
      if (!back) {
        isoBoxAt(ctx, sx, sy, w * 0.4, -w * 0.4 * 0.5 - 12, w * 0.2, 2, 4, "#D4B040", "#C4A030", "#E4C050");
      } else {
        const latch = off(w * 0.4, d - 2);
        isoBoxAt(ctx, sx, sy, latch.ox, latch.oy - 12, w * 0.2, 2, 4, "#D4B040", "#C4A030", "#E4C050");
      }
      break;
    }

    // ── Floor Lamp ─────────────────────────────────────────────────────────
    case "floor_lamp": {
      // Elegant floor lamp — weighted base, thin stem, fabric shade
      // Heavy circular base
      isoBox(ctx, sx, sy, 12, 12, 3, "#706058", "#605048", "#807068");
      isoBox(ctx, sx, sy, 10, 10, 4, "#807060", "#706050", "#908070");
      // Pole — thin, centered
      isoBoxAt(ctx, sx, sy, 5, -5 * 0.5, 2, 2, 50, "#907060", "#806050", "#A08070");
      // Shade bracket
      isoBoxAt(ctx, sx, sy, 3, -3 * 0.5 - 48, 6, 6, 2, "#808080", "#707070", "#909090");
      // Shade — warm fabric, tapers slightly
      if (on) {
        // Inner glow (behind shade)
        ctx.globalAlpha = 0.12;
        isoBoxAt(ctx, sx, sy, -5, -52, 22, 22, 16, "#FFE060", "#FFD840", "#FFE880");
        ctx.globalAlpha = 1;
        // Shade body
        isoBoxAt(ctx, sx, sy, -2, -50 + 1, 16, 16, 12, "#F0D880", "#E8C86A", "#F8E090");
        // Top ring
        isoBoxAt(ctx, sx, sy, -1, -50 - 11, 14, 14, 1, "#E8D070", "#E0C860", "#F0D880");
      } else {
        isoBoxAt(ctx, sx, sy, -2, -50 + 1, 16, 16, 12, "#B0A080", "#A09070", "#C0B090");
        isoBoxAt(ctx, sx, sy, -1, -50 - 11, 14, 14, 1, "#A89878", "#988868", "#B8A888");
      }
      break;
    }

    // ── Candle ─────────────────────────────────────────────────────────────
    case "candle": {
      // Small table candle — holder base, wax column, flame
      isoBox(ctx, sx, sy, 8, 8, 3, "#C0A050", "#B09040", "#D0B060");
      // Saucer/holder rim
      isoBoxAt(ctx, sx, sy, -1, -3 + 0.5, 10, 10, 1.5, "#D0B060", "#C0A050", "#E0C070");
      // Wax column
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 3, 4, 4, 16, "#F0ECE0", "#E8E4D8", "#F8F4E8");
      if (on) {
        const flicker = Math.sin(time * 0.008) * 1;
        // Outer flame
        isoBoxAt(ctx, sx, sy, 2.5, -2.5 * 0.5 - 19 - flicker, 3, 3, 5 + flicker, "#FFB020", "#FFA010", "#FFC030");
        // Inner bright core
        isoBoxAt(ctx, sx, sy, 3, -3 * 0.5 - 19.5 - flicker, 2, 2, 4 + flicker, "#FFF060", "#FFE850", "#FFF870");
        // Glow bleed
        ctx.globalAlpha = 0.1;
        isoBoxAt(ctx, sx, sy, -2, -18, 12, 12, 10, "#FFD040", "#FFC030", "#FFE050");
        ctx.globalAlpha = 1;
      }
      break;
    }

    // ── Fireplace ──────────────────────────────────────────────────────────
    case "fireplace": {
      // Substantial stone hearth. back=true: viewing from behind (no firebox/flames visible).
      const { w, d, back } = isoRotate(sx, sy, 28, 14, direction);
      const off = (e: number, m: number) => ({ ox: e - m, oy: -(e + m) * 0.5 });

      // Stone base/hearth
      isoBox(ctx, sx, sy, w + 2, d + 3, 4, "#888888", "#787878", "#989898");
      // Main stone body
      isoBox(ctx, sx, sy, w, d, 38, "#7A7A7A", "#6A6A6A", "#8A8A8A");
      // Stone texture
      ctx.globalAlpha = 0.06;
      for (let course = 0; course < 5; course++) {
        isoBoxAt(ctx, sx, sy, 0, -(4 + course * 7), w, d, 1, "#000", "#000", "#000");
      }
      ctx.globalAlpha = 1;
      // Mantle
      isoBoxAt(ctx, sx, sy, -3, -3 * 0.5 - 38 + 1.5, w + 6, d + 4, 4, "#8B6035", "#7B5025", "#9B7045");
      isoBoxAt(ctx, sx, sy, -3, -3 * 0.5 - 38 + 1.5, w + 6, d + 4, 5, "#9B7045", "#8B6035", "#AB8055");

      if (!back) {
        // NORMAL: firebox + flames face camera (south)
        isoBoxAt(ctx, sx, sy, w * 0.18, -w * 0.18 * 0.5, w * 0.64, d * 0.5, 26, "#1A1010", "#0A0808", "#2A1818");
        if (on) {
          const t = time * 0.007;
          ctx.globalAlpha = 0.3;
          isoBoxAt(ctx, sx, sy, w * 0.2, -w * 0.2 * 0.5, w * 0.6, d * 0.35, 4, "#FF4010", "#FF3008", "#FF5018");
          ctx.globalAlpha = 1;
          isoBoxAt(ctx, sx, sy, w * 0.22, -w * 0.22 * 0.5, w * 0.5, d * 0.3, 7, "#7A4820", "#5A3010", "#8A5830");
          isoBoxAt(ctx, sx, sy, w * 0.3, -w * 0.3 * 0.5, w * 0.35, d * 0.25, 5, "#6A3818", "#4A2808", "#7A4828");
          const f1 = Math.sin(t) * 2;
          const f2 = Math.sin(t + 1.5) * 2;
          const f3 = Math.sin(t * 1.3 + 0.7) * 1.5;
          isoBoxAt(ctx, sx, sy, w * 0.25, -w * 0.25 * 0.5 - 7 - f1, w * 0.12, d * 0.2, 12 + f1, "#FF8020", "#FF6010", "#FFA030");
          isoBoxAt(ctx, sx, sy, w * 0.38, -w * 0.38 * 0.5 - 7 - f2, w * 0.16, d * 0.22, 16 + f2, "#FFA020", "#FF8010", "#FFB030");
          isoBoxAt(ctx, sx, sy, w * 0.56, -w * 0.56 * 0.5 - 7 - f3, w * 0.10, d * 0.18, 10 + f3, "#FF6010", "#FF4000", "#FF8020");
          ctx.globalAlpha = 0.6;
          isoBoxAt(ctx, sx, sy, w * 0.36, -w * 0.36 * 0.5 - 10, w * 0.14, d * 0.15, 8 + f2, "#FFD040", "#FFC030", "#FFE050");
          ctx.globalAlpha = 1;
        }
      }
      // BACK: plain stone back — firebox faces away, no flames visible. Just the stone body + mantle.
      break;
    }

    // ── Chandelier ─────────────────────────────────────────────────────────
    case "chandelier": {
      // Ceiling-hung fixture — elevated iso rendering
      const { w, d: cd } = isoRotate(sx, sy, 16, 16, direction);
      // Chain from ceiling
      isoBoxAt(ctx, sx, sy, w * 0.4, -w * 0.4 * 0.5 - 56, 2, 2, 14, "#A09070", "#908060", "#B0A080");
      // Central body ring
      isoBoxAt(ctx, sx, sy, 0, -44, w, cd, 4, "#C0A050", "#B09040", "#D0B060");
      // Arms + candles (4 directions)
      const arms: [number, number][] = [[w * 0.05, -(w * 0.05) * 0.5], [w * 0.7, -(w * 0.7) * 0.5], [-cd * 0.3, -(cd * 0.3) * 0.5], [w * 0.4 - cd * 0.3, -w * 0.4 * 0.5 - (cd * 0.3) * 0.5]];
      for (const [ax, ay] of arms) {
        isoBoxAt(ctx, sx, sy, ax, ay - 44, 3, 3, 2, "#B09040", "#A08030", "#C0A050");
        if (on) {
          isoBoxAt(ctx, sx, sy, ax + 0.5, ay - 44 - 2, 2, 2, 4, "#FFD860", "#FFC840", "#FFE880");
          isoBoxAt(ctx, sx, sy, ax + 0.5, ay - 44 - 5, 1.5, 1.5, 3, "#FFF0A0", "#FFE890", "#FFFAB0");
        }
      }
      // Crystal drops
      if (on) {
        ctx.globalAlpha = 0.4;
        isoBoxAt(ctx, sx, sy, -2, -38, w + 4, cd + 4, 3, "#A0D8FF", "#90C8EF", "#B0E8FF");
        ctx.globalAlpha = 1;
      }
      break;
    }

    // ── Potted Plant ───────────────────────────────────────────────────────
    case "potted_plant": {
      // Pot (small square base)
      isoBox(ctx, sx, sy, 12, 12, 14, "#C05030", "#A04020", "#D06040");
      // Pot rim
      isoBoxAt(ctx, sx, sy, -1, -14 + 0.5, 14, 14, 2, "#D06040", "#B05030", "#E07050");
      // Soil
      isoBoxAt(ctx, sx, sy, 1, -14 - 0.5, 10, 10, 2, "#5A3018", "#4A2008", "#6A4028");
      // Stem (thin tall box)
      isoBoxAt(ctx, sx, sy, 5, -5 * 0.5 - 16, 2, 2, 18, "#508030", "#407020", "#609040");
      // Leaves (overlapping green iso boxes at various heights)
      isoBoxAt(ctx, sx, sy, -2, -32, 10, 8, 4, "#70B050", "#60A040", "#80C060");
      isoBoxAt(ctx, sx, sy, 6, -6 * 0.5 - 36, 8, 10, 4, "#60A040", "#508030", "#70B050");
      isoBoxAt(ctx, sx, sy, 0, -28, 8, 6, 3, "#508038", "#407028", "#609048");
      isoBoxAt(ctx, sx, sy, 8, -8 * 0.5 - 30, 6, 8, 3, "#68A848", "#589838", "#78B858");
      break;
    }

    // ── Rug ────────────────────────────────────────────────────────────────
    case "rug_woven": {
      // Floor item: nested iso diamonds (top faces at h=0)
      const { w, d } = isoRotate(sx, sy, 48, 48, direction);
      // Helper to draw a centered diamond at a given scale
      const rugDiamond = (scale: number, color: string) => {
        const rw = w * scale, rd = d * scale;
        // Offset to center within the full diamond
        const ew = (w - rw) / 2; // half-gap along east axis
        const ed = (d - rd) / 2; // half-gap along west axis
        // Screen offset: east by ew, west by ed
        const ox = ew - ed;
        const oy = -(ew + ed) * 0.5;
        isoTop(ctx, sx + ox, sy + oy, rw, rd, 0, color);
      };
      rugDiamond(1.0, "#B87848");
      rugDiamond(0.88, "#C88858");
      rugDiamond(0.75, "#D09868");
      rugDiamond(0.38, "#C07040");
      rugDiamond(0.24, "#A85830");
      rugDiamond(0.12, "#E0B060");
      break;
    }

    // ── Window ─────────────────────────────────────────────────────────────
    case "window_frame": {
      const { w, d } = isoRotate(sx, sy, 26, 4, direction);
      // Outer frame — thick wooden surround
      isoBox(ctx, sx, sy, w, d, 40, "#8B6035", "#7A5025", "#9B7045");
      // Inner frame recess
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 2, w - 4, d * 0.6, 34, "#5A3E20", "#4A2E10", "#6A4E30");
      // Glass panes — left and right, split by mullion
      const glassColor = on ? "#A8D8F0" : "#808090";
      const glassColorL = on ? "#98C8E0" : "#707080";
      const glassColorR = on ? "#B8E8FF" : "#909098";
      const paneW = (w - 7) * 0.5;
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 3, paneW, d * 0.3, 32, glassColor, glassColorL, glassColorR);
      isoBoxAt(ctx, sx, sy, 2 + paneW + 3, -(2 + paneW + 3) * 0.5 - 3, paneW, d * 0.3, 32, glassColor, glassColorL, glassColorR);
      // Vertical mullion (centered east offset, pure east oy)
      const mullOx = w * 0.5 - 1.5;
      isoBoxAt(ctx, sx, sy, mullOx, -mullOx * 0.5 - 2, 3, d * 0.5, 36, "#9B7045", "#8B6035", "#AB8055");
      // Horizontal mullion
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 18, w - 4, d * 0.5, 2, "#9B7045", "#8B6035", "#AB8055");
      // Sill — wider than frame, slight overhang
      isoBox(ctx, sx, sy, w + 4, d + 3, 3, "#A07848", "#906838", "#B08858");
      // Sill top highlight
      isoBox(ctx, sx, sy, w + 4, d + 3, 4, "#B08858", "#A07848", "#C09868");
      // Light glow when daytime
      if (on) {
        ctx.globalAlpha = 0.08;
        isoBoxAt(ctx, sx, sy, 0, -2, w, d + 6, 38, "#C8E8FF", "#B8D8EF", "#D8F8FF");
        ctx.globalAlpha = 1;
      }
      break;
    }

    // ── Fish Tank ──────────────────────────────────────────────────────────
    case "fish_tank": {
      const { w, d } = isoRotate(sx, sy, 22, 14, direction);
      // Stand — sturdy wooden base
      isoBox(ctx, sx, sy, w, d, 10, "#706050", "#605040", "#807060");
      // Stand top rail
      isoBox(ctx, sx, sy, w + 1, d + 1, 11, "#807060", "#706050", "#908070");
      // Tank body — glass with visible thickness
      isoBoxAt(ctx, sx, sy, 0, -11, w, d, 24, "#80C0D8", "#70B0C8", "#90D0E8");
      // Inner water volume
      isoBoxAt(ctx, sx, sy, 1, -1 * 0.5 - 12, w - 2, d - 2, 20, "#60A8C8", "#5098B8", "#70B8D8");
      // Gravel bed at bottom
      isoBoxAt(ctx, sx, sy, 1, -1 * 0.5 - 11, w - 2, d - 2, 4, "#907050", "#806040", "#A08060");
      // Aquatic plants
      isoBoxAt(ctx, sx, sy, 3, -3 * 0.5 - 15, 3, 3, 12, "#408030", "#307020", "#509040");
      isoBoxAt(ctx, sx, sy, w - 7, -(w - 7) * 0.5 - 14, 3, 4, 10, "#508040", "#407030", "#609050");
      // Fish — animated positions
      if (on) {
        const f1x = 4 + Math.sin(time * 0.002) * 5;
        const f1y = -20 - Math.cos(time * 0.0015) * 3;
        isoBoxAt(ctx, sx, sy, f1x, -f1x * 0.5 + f1y, 4, 2, 2, "#FF8040", "#FF6020", "#FFA060");
        const f2x = w * 0.5 + Math.sin(time * 0.0018 + 2) * 4;
        const f2y = -24 - Math.cos(time * 0.002 + 1) * 2;
        isoBoxAt(ctx, sx, sy, f2x, -f2x * 0.5 + f2y, 3, 2, 2, "#4080FF", "#2060E0", "#60A0FF");
      }
      // Bubbles (tiny bright boxes)
      isoBoxAt(ctx, sx, sy, w * 0.4, -w * 0.4 * 0.5 - 30, 1.5, 1.5, 1.5, "#E8F4FF", "#D8E4EF", "#F8FFFF");
      isoBoxAt(ctx, sx, sy, w * 0.6, -w * 0.6 * 0.5 - 26, 1, 1, 1, "#E8F4FF", "#D8E4EF", "#F8FFFF");
      // Tank rim — top edge
      isoBoxAt(ctx, sx, sy, -0.5, -34.5, w + 1, d + 1, 1.5, "#90D0E8", "#80C0D8", "#A0E0F8");
      break;
    }

    // ── Wall Art ───────────────────────────────────────────────────────────
    case "wall_art": {
      const { w, d } = isoRotate(sx, sy, 22, 3, direction);
      // Outer frame — dark ornate wood
      isoBox(ctx, sx, sy, w, d, 30, "#5A4020", "#4A3010", "#6A5030");
      // Inner frame — lighter wood
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 2, w - 4, d * 0.8, 24, "#8B6030", "#7B5020", "#9B7040");
      // Canvas surface
      isoBoxAt(ctx, sx, sy, 3, -3 * 0.5 - 3, w - 6, d * 0.5, 22, "#F0EAD8", "#E0DAC8", "#FFF4E8");
      // Abstract art — warm blocks of color on the canvas face
      isoBoxAt(ctx, sx, sy, 4, -4 * 0.5 - 5, 6, d * 0.3, 14, "#D07040", "#C06030", "#E08050");
      isoBoxAt(ctx, sx, sy, 10, -10 * 0.5 - 4, 5, d * 0.3, 10, "#80A0C0", "#7090B0", "#90B0D0");
      isoBoxAt(ctx, sx, sy, 6, -6 * 0.5 - 16, 8, d * 0.3, 6, "#E0C060", "#D0B050", "#F0D070");
      isoBoxAt(ctx, sx, sy, 4, -4 * 0.5 - 12, 4, d * 0.3, 5, "#C06040", "#B05030", "#D07050");
      // Frame highlight on top edge
      isoBoxAt(ctx, sx, sy, 0, -30, w, d, 1, "#7A5A38", "#6A4A28", "#8A6A48");
      break;
    }

    // ── Aeterna Device ─────────────────────────────────────────────────────
    case "aeterna_device": {
      const t = time * 0.003;
      // Stand base
      isoBox(ctx, sx, sy, 10, 10, 4, "#404060", "#303040", "#505070");
      // Stand neck
      isoBoxAt(ctx, sx, sy, 3, -3 * 0.5, 4, 4, 8, "#404060", "#303050", "#505070");
      // Body (tall, thin)
      isoBox(ctx, sx, sy, 12, 4, 36, "#242440", "#1A1A2E", "#2E2E50");
      // Screen (front face glow)
      const pulse = Math.sin(t) * 0.3 + 0.7;
      ctx.globalAlpha = pulse;
      isoBoxAt(ctx, sx, sy, 1, -8, 10, 1, 22, "#60A8FF", "#5098EF", "#70B8FF");
      ctx.globalAlpha = 1;
      // Status light at top
      const statusColor = (Math.sin(t * 2) > 0) ? "#60FFB0" : "#20C060";
      isoBoxAt(ctx, sx, sy, 4, -4 * 0.5 - 34, 4, 2, 2, statusColor, statusColor, statusColor);
      // Subtle glow bleed
      ctx.globalAlpha = 0.12 * pulse;
      isoBoxAt(ctx, sx, sy, -3, -6, 18, 10, 30, "#4080FF", "#3070EF", "#5090FF");
      ctx.globalAlpha = 1;
      break;
    }

    // ── Computer ───────────────────────────────────────────────────────────
    case "computer_desk": {
      // Monitor, stand, and keyboard — all isometric
      const { w, d: cd } = isoRotate(sx, sy, 18, 6, direction);
      // Monitor stand base
      isoBox(ctx, sx, sy, w * 0.5, cd, 2, "#404050", "#303040", "#505060");
      // Stand neck
      isoBoxAt(ctx, sx, sy, w * 0.35, -w * 0.35 * 0.5, 4, 3, 6, "#353545", "#252535", "#454555");
      // Monitor body
      isoBoxAt(ctx, sx, sy, 0, -8, w, cd * 0.6, 22, "#202030", "#181828", "#282838");
      // Screen
      if (on) {
        isoBoxAt(ctx, sx, sy, 1, -10, w - 2, cd * 0.3, 18, "#2830A0", "#182090", "#3840B0");
        ctx.globalAlpha = 0.3;
        isoBoxAt(ctx, sx, sy, 2, -12, w - 4, cd * 0.2, 8, "#5060D0", "#4050C0", "#6070E0");
        ctx.globalAlpha = 1;
      }
      // Keyboard
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 + 4, w - 4, cd * 1.2, 1.5, "#3A3A4A", "#2A2A3A", "#4A4A5A");
      break;
    }

    // ── Record Player ──────────────────────────────────────────────────────
    case "record_player": {
      const { w, d } = isoRotate(sx, sy, 22, 16, direction);
      // Wooden case body
      isoBox(ctx, sx, sy, w, d, 8, "#6B4F2E", "#5B3F1E", "#7B5F3E");
      // Case top surface with inset
      isoBox(ctx, sx, sy, w, d, 10, "#7A5A38", "#6A4A28", "#8A6A48");
      // Platter well (dark recessed circle area)
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 10, w * 0.65, d * 0.7, 1, "#1A1A1A", "#0A0A0A", "#2A2A2A");
      // Record on platter
      isoBoxAt(ctx, sx, sy, 3, -3 * 0.5 - 10.5, w * 0.6, d * 0.6, 1, "#2A2A2A", "#1A1A1A", "#3A3A3A");
      if (on) {
        // Spinning groove shimmer
        ctx.globalAlpha = 0.25;
        isoBoxAt(ctx, sx, sy, 4, -4 * 0.5 - 10.5, w * 0.5, d * 0.5, 1, "#404040", "#303030", "#505050");
        ctx.globalAlpha = 1;
      }
      // Record label
      isoBoxAt(ctx, sx, sy, w * 0.22, -w * 0.22 * 0.5 - 11, w * 0.2, d * 0.2, 1.5, "#C04040", "#B03030", "#D05050");
      // Tonearm base
      isoBoxAt(ctx, sx, sy, w * 0.7, -w * 0.7 * 0.5 - 10, 4, 3, 3, "#C0A060", "#B09050", "#D0B070");
      // Tonearm
      isoBoxAt(ctx, sx, sy, w * 0.55, -w * 0.55 * 0.5 - 11, 2, 8, 2, "#C0A060", "#B09050", "#D0B070");
      // Tonearm head
      isoBoxAt(ctx, sx, sy, w * 0.52, -w * 0.52 * 0.5 - 11, 3, 2, 2.5, "#B09050", "#A08040", "#C0A060");
      // Knobs
      isoBoxAt(ctx, sx, sy, 1, -1 * 0.5 - 8, 3, 3, 3, "#909090", "#808080", "#A0A0A0");
      break;
    }

    // ── Upright Piano ──────────────────────────────────────────────────────
    case "piano_upright": {
      const { w, d } = isoRotate(sx, sy, 50, 18, direction);
      // Legs — two sturdy front legs
      isoBox(ctx, sx, sy, 4, 4, 6, "#1A1A1A", "#101010", "#242424");
      isoBoxAt(ctx, sx, sy, w - 4, -(w - 4) * 0.5, 4, 4, 6, "#1A1A1A", "#101010", "#242424");
      // Main body — tall, lacquered black
      isoBox(ctx, sx, sy, w, d, 48, "#1A1A1A", "#101010", "#242424");
      // Body panel detail (lighter inset)
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 6, w - 4, d * 0.15, 38, "#242424", "#1A1A1A", "#2E2E2E");
      // Key bed — the shelf where keys sit, slightly protruding
      isoBox(ctx, sx, sy, w + 2, d * 0.7, 18, "#2A2A2A", "#1A1A1A", "#3A3A3A");
      // White keys — a row across the front face
      const keyCount = 12;
      const keyW = (w - 2) / keyCount;
      for (let k = 0; k < keyCount; k++) {
        isoBoxAt(ctx, sx, sy, 1 + k * keyW, -(1 + k * keyW) * 0.5 - 18, keyW * 0.85, d * 0.35, 1.5, "#F0EEE8", "#E0DED8", "#FFFFF8");
      }
      // Black keys — pattern follows real piano
      const blackKeys = [1,1,0,1,1,1,0];
      for (let k = 0; k < keyCount; k++) {
        if (blackKeys[k % 7]) {
          isoBoxAt(ctx, sx, sy, 1.5 + k * keyW + keyW * 0.3, -(1.5 + k * keyW + keyW * 0.3) * 0.5 - 18, keyW * 0.5, d * 0.2, 3, "#1A1A1A", "#0A0A0A", "#2A2A2A");
        }
      }
      // Music stand (thin shelf above keys)
      isoBoxAt(ctx, sx, sy, 4, -4 * 0.5 - 32, w - 8, d * 0.3, 2, "#303030", "#202020", "#404040");
      // Music stand lip
      isoBoxAt(ctx, sx, sy, 6, -6 * 0.5 - 28, w - 12, d * 0.15, 1, "#282828", "#181818", "#383838");
      // Top lid
      isoBox(ctx, sx, sy, w, d, 50, "#222222", "#161616", "#2E2E2E");
      // Lacquer highlight
      ctx.globalAlpha = 0.06;
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 46, w * 0.4, d * 0.5, 1, "#FFFFFF", "#FFFFFF", "#FFFFFF");
      ctx.globalAlpha = 1;
      break;
    }

    // ── Shelf Speaker ──────────────────────────────────────────────────────
    case "speaker_shelf": {
      const { w, d } = isoRotate(sx, sy, 14, 12, direction);
      // Cabinet body — matte black box
      isoBox(ctx, sx, sy, w, d, 24, "#2A2A2A", "#1A1A1A", "#3A3A3A");
      // Grille cloth (front face — slightly lighter)
      isoBoxAt(ctx, sx, sy, 1, -1 * 0.5 - 2, w - 2, d * 0.15, 18, "#333333", "#232323", "#434343");
      // Woofer cone (lower, larger)
      isoBoxAt(ctx, sx, sy, w * 0.2, -w * 0.2 * 0.5 - 4, w * 0.6, d * 0.3, 8, "#1A1A1A", "#0A0A0A", "#2A2A2A");
      isoBoxAt(ctx, sx, sy, w * 0.3, -w * 0.3 * 0.5 - 5, w * 0.4, d * 0.2, 6, "#222222", "#121212", "#323232");
      // Dust cap center
      isoBoxAt(ctx, sx, sy, w * 0.38, -w * 0.38 * 0.5 - 6, w * 0.24, d * 0.15, 5, "#2A2A2A", "#1A1A1A", "#3A3A3A");
      // Tweeter (upper, smaller)
      isoBoxAt(ctx, sx, sy, w * 0.3, -w * 0.3 * 0.5 - 16, w * 0.4, d * 0.25, 4, "#1A1A1A", "#0A0A0A", "#2A2A2A");
      isoBoxAt(ctx, sx, sy, w * 0.38, -w * 0.38 * 0.5 - 17, w * 0.24, d * 0.15, 3, "#252525", "#151515", "#353535");
      // Status LED
      const ledColor = on ? "#00FF80" : "#404040";
      isoBoxAt(ctx, sx, sy, w * 0.42, -w * 0.42 * 0.5 - 1, 2, 1, 2, ledColor, ledColor, ledColor);
      // Top surface highlight
      isoBox(ctx, sx, sy, w, d, 25, "#303030", "#202020", "#404040");
      break;
    }

    // ── Arcade Cabinet ─────────────────────────────────────────────────────
    case "arcade_cabinet": {
      const { w, d } = isoRotate(sx, sy, 18, 14, direction);
      const t = time * 0.004;
      // Legs — short sturdy feet
      isoBox(ctx, sx, sy, 4, 4, 4, "#181820", "#101018", "#282830");
      isoBoxAt(ctx, sx, sy, w - 4, -(w - 4) * 0.5, 4, 4, 4, "#181820", "#101018", "#282830");
      // Main body — deep purple/navy cabinet
      isoBox(ctx, sx, sy, w, d, 50, "#202040", "#181830", "#282850");
      // Control panel — angled shelf at waist height
      isoBox(ctx, sx, sy, w + 1, d * 0.6, 22, "#181830", "#101028", "#202040");
      // Joystick
      isoBoxAt(ctx, sx, sy, 3, -3 * 0.5 - 22, 3, 3, 5, "#C03030", "#B02020", "#D04040");
      // Buttons
      isoBoxAt(ctx, sx, sy, 8, -8 * 0.5 - 22, 3, 2, 3, "#F0F040", "#E0E030", "#FFFF50");
      isoBoxAt(ctx, sx, sy, 12, -12 * 0.5 - 22, 2, 2, 3, "#40F040", "#30E030", "#50FF50");
      // Screen area — recessed dark panel
      isoBoxAt(ctx, sx, sy, 1, -1 * 0.5 - 26, w - 2, d * 0.3, 18, "#0A0A20", "#060618", "#101030");
      // Screen content
      if (on) {
        const screenColors = ["#FF4040","#40FF40","#4040FF","#FFD040"];
        const sc = screenColors[Math.floor(t / 2) % screenColors.length];
        isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 28, w - 4, d * 0.2, 14, sc, sc, sc);
        // Scanline
        ctx.globalAlpha = 0.4;
        const scanY = Math.sin(t) * 4;
        isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 34 + scanY, w - 4, d * 0.1, 2, "#FFFFFF", "#FFFFFF", "#FFFFFF");
        ctx.globalAlpha = 1;
      }
      // Marquee header — lit sign at top
      isoBoxAt(ctx, sx, sy, -1, -48, w + 2, d + 2, 5, "#1A1A38", "#101030", "#2A2A48");
      ctx.globalAlpha = on ? 0.8 : 0.2;
      isoBoxAt(ctx, sx, sy, 0, -47, w, d * 0.6, 3, "#FF80C0", "#FF60B0", "#FFA0D0");
      ctx.globalAlpha = 1;
      // Top cap
      isoBoxAt(ctx, sx, sy, -1, -53, w + 2, d + 2, 2, "#282850", "#202040", "#303060");
      // Screen glow
      if (on) {
        ctx.globalAlpha = 0.06;
        isoBoxAt(ctx, sx, sy, -2, -26, w + 4, d + 4, 20, "#4040FF", "#3030EF", "#5050FF");
        ctx.globalAlpha = 1;
      }
      break;
    }

    // ── Chess Table ────────────────────────────────────────────────────────
    case "chess_table": {
      const { w, d } = isoRotate(sx, sy, 22, 22, direction);
      // Four turned legs
      const cLeg = 2.5;
      isoBox(ctx, sx, sy, cLeg, cLeg, 14, "#6B4F2E", "#5B3F1E", "#7B5F3E");
      isoBoxAt(ctx, sx, sy, w - cLeg, -(w - cLeg) * 0.5, cLeg, cLeg, 14, "#6B4F2E", "#5B3F1E", "#7B5F3E");
      isoBoxAt(ctx, sx, sy, -d + cLeg, -(d - cLeg) * 0.5, cLeg, cLeg, 14, "#6B4F2E", "#5B3F1E", "#7B5F3E");
      isoBoxAt(ctx, sx, sy, w - cLeg - d + cLeg, -(w - cLeg) * 0.5 - (d - cLeg) * 0.5, cLeg, cLeg, 14, "#6B4F2E", "#5B3F1E", "#7B5F3E");
      // Tabletop frame
      isoBox(ctx, sx, sy, w, d, 16, "#8B6035", "#7B5025", "#9B7045");
      isoBox(ctx, sx, sy, w, d, 17.5, "#9B7045", "#8B6035", "#AB8055");
      // Chess board — 4×4 visible squares on the top face
      const sqW = (w - 4) / 4;
      const sqD = (d - 4) / 4;
      for (let row = 0; row < 4; row++) {
        for (let col = 0; col < 4; col++) {
          const isLight = (row + col) % 2 === 0;
          const sqOx = 2 + col * sqW - row * sqD;
          const sqOy = -(2 + col * sqW) * 0.5 - (row * sqD) * 0.5 - 17.5;
          const c = isLight ? "#F0DDB0" : "#4A3010";
          isoBoxAt(ctx, sx, sy, sqOx, sqOy, sqW, sqD, 0.5, c, c, c);
        }
      }
      break;
    }

    // ── Cooking Stove ──────────────────────────────────────────────────────
    case "cooking_stove": {
      const { w, d } = isoRotate(sx, sy, 24, 20, direction);
      const cooking = on && state.cooking;
      // Main body — solid steel
      isoBox(ctx, sx, sy, w, d, 28, "#606060", "#505050", "#707070");
      // Oven door (front face, lower half)
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 4, w - 4, d * 0.15, 14, "#404040", "#303030", "#505050");
      // Oven door window
      isoBoxAt(ctx, sx, sy, 4, -4 * 0.5 - 6, w - 8, d * 0.08, 10, "#383838", "#282828", "#484848");
      // Oven handle
      isoBoxAt(ctx, sx, sy, 5, -5 * 0.5 - 18, w - 10, d * 0.08, 2, "#888888", "#787878", "#989898");
      // Stovetop surface
      isoBox(ctx, sx, sy, w, d, 30, "#484848", "#383838", "#585858");
      // Burner grates — left and right
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 30, w * 0.38, d * 0.5, 1.5, "#383838", "#282828", "#484848");
      isoBoxAt(ctx, sx, sy, w * 0.55, -w * 0.55 * 0.5 - 30, w * 0.38, d * 0.5, 1.5, "#383838", "#282828", "#484848");
      if (cooking) {
        // Flame glow under grates
        ctx.globalAlpha = 0.6;
        isoBoxAt(ctx, sx, sy, 3, -3 * 0.5 - 29, w * 0.34, d * 0.4, 1, "#FF6020", "#FF4010", "#FF8030");
        isoBoxAt(ctx, sx, sy, w * 0.57, -w * 0.57 * 0.5 - 29, w * 0.34, d * 0.4, 1, "#FF8020", "#FF6010", "#FFA030");
        ctx.globalAlpha = 1;
      }
      // Control knobs — row along front, above oven
      for (let k = 0; k < 4; k++) {
        const kx = 3 + k * (w - 6) / 4;
        isoBoxAt(ctx, sx, sy, kx, -kx * 0.5 - 22, 3, 2, 3, "#707070", "#606060", "#808080");
        isoBoxAt(ctx, sx, sy, kx + 0.5, -(kx + 0.5) * 0.5 - 22.5, 2, 1, 2, "#505050", "#404040", "#606060");
      }
      break;
    }

    // ── Kitchen Counter ────────────────────────────────────────────────────
    case "kitchen_counter": {
      const { w, d } = isoRotate(sx, sy, 24, 18, direction);
      // Counter body — warm wood/laminate
      isoBox(ctx, sx, sy, w, d, 24, "#D0C0A8", "#C0B098", "#E0D0B8");
      // Cabinet door panel — inset
      isoBoxAt(ctx, sx, sy, 3, -3 * 0.5 - 4, w - 6, d * 0.15, 14, "#B8A890", "#A89880", "#C8B8A0");
      // Cabinet inner panel
      isoBoxAt(ctx, sx, sy, 4, -4 * 0.5 - 5, w - 8, d * 0.1, 12, "#C8B8A0", "#B8A890", "#D8C8B0");
      // Cabinet handle
      isoBoxAt(ctx, sx, sy, w * 0.35, -w * 0.35 * 0.5 - 12, w * 0.3, d * 0.08, 2, "#909090", "#808080", "#A0A0A0");
      // Countertop — overhangs slightly, polished stone/marble
      isoBox(ctx, sx, sy, w + 2, d + 2, 26, "#E8D8C0", "#D8C8B0", "#F0E0C8");
      // Countertop surface highlight
      isoBox(ctx, sx, sy, w + 2, d + 2, 27, "#F0E0C8", "#E0D0B8", "#F8E8D0");
      // Subtle marble veining
      ctx.globalAlpha = 0.04;
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 27, w * 0.6, d * 0.3, 0.5, "#A08060", "#A08060", "#A08060");
      ctx.globalAlpha = 1;
      break;
    }

    // ── Potted Tree ────────────────────────────────────────────────────────
    case "tree_potted": {
      // Large pot
      isoBox(ctx, sx, sy, 16, 16, 16, "#C05030", "#A04020", "#D06040");
      // Pot rim
      isoBoxAt(ctx, sx, sy, -1, -16 + 0.5, 18, 18, 3, "#D06040", "#B05030", "#E07050");
      // Soil
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 16, 12, 12, 2, "#5A3018", "#4A2008", "#6A4028");
      // Trunk — thick, woody
      isoBoxAt(ctx, sx, sy, 6, -6 * 0.5 - 18, 4, 4, 26, "#7A5020", "#6A4010", "#8A6030");
      // Branch fork
      isoBoxAt(ctx, sx, sy, 4, -4 * 0.5 - 38, 3, 3, 8, "#6A4818", "#5A3808", "#7A5828");
      isoBoxAt(ctx, sx, sy, 9, -9 * 0.5 - 36, 3, 3, 6, "#6A4818", "#5A3808", "#7A5828");
      // Foliage clusters — overlapping green masses
      isoBoxAt(ctx, sx, sy, -2, -42, 14, 12, 8, "#488030", "#387020", "#589040");
      isoBoxAt(ctx, sx, sy, 6, -6 * 0.5 - 48, 12, 14, 8, "#58A040", "#489030", "#68B050");
      isoBoxAt(ctx, sx, sy, -6, -36, 10, 8, 6, "#409030", "#308020", "#50A040");
      isoBoxAt(ctx, sx, sy, 12, -12 * 0.5 - 38, 10, 8, 6, "#50A038", "#409028", "#60B048");
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 34, 12, 10, 5, "#508030", "#407020", "#609040");
      // Top tuft
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 52, 10, 10, 5, "#60B048", "#50A038", "#70C058");
      break;
    }

    // ── Garden Fountain ────────────────────────────────────────────────────
    case "garden_fountain": {
      const { w, d: fd } = isoRotate(sx, sy, 48, 48, direction);
      const t = time * 0.004;
      // Large octagonal basin — stone
      isoBox(ctx, sx, sy, w, fd, 10, "#9090A0", "#808090", "#A0A0B0");
      // Basin inner wall
      isoBoxAt(ctx, sx, sy, 3, -3 * 0.5 - 4, w - 6, fd - 6, 8, "#7A7A8A", "#6A6A7A", "#8A8A9A");
      // Water surface in basin
      isoBoxAt(ctx, sx, sy, 4, -4 * 0.5 - 8, w - 8, fd - 8, 1, "#5090C0", "#4080B0", "#60A0D0");
      // Ripple effect
      if (on) {
        ctx.globalAlpha = 0.15;
        isoBoxAt(ctx, sx, sy, 6, -6 * 0.5 - 8.5, w - 12, fd - 12, 0.5, "#A0D8F0", "#90C8E0", "#B0E8FF");
        ctx.globalAlpha = 1;
      }
      // Central column — stone pillar
      isoBoxAt(ctx, sx, sy, w * 0.35, -w * 0.35 * 0.5 - 10, 8, 8, 34, "#A0A0B0", "#9090A0", "#B0B0C0");
      // Column detail rings
      isoBoxAt(ctx, sx, sy, w * 0.33, -w * 0.33 * 0.5 - 14, 10, 10, 2, "#B0B0C0", "#A0A0B0", "#C0C0D0");
      isoBoxAt(ctx, sx, sy, w * 0.33, -w * 0.33 * 0.5 - 28, 10, 10, 2, "#B0B0C0", "#A0A0B0", "#C0C0D0");
      // Upper bowl — smaller basin atop column
      isoBoxAt(ctx, sx, sy, w * 0.25, -w * 0.25 * 0.5 - 38, 16, 16, 6, "#9090A0", "#808090", "#A0A0B0");
      // Water in upper bowl
      isoBoxAt(ctx, sx, sy, w * 0.28, -w * 0.28 * 0.5 - 40, 12, 12, 1, "#6080A0", "#5070A0", "#7090B0");
      // Water jet from top
      if (on) {
        ctx.globalAlpha = 0.6 + Math.sin(t) * 0.2;
        // Central spout
        isoBoxAt(ctx, sx, sy, w * 0.37, -w * 0.37 * 0.5 - 44, 3, 3, 8 + Math.sin(t) * 2, "#80C0E0", "#70B0D0", "#90D0F0");
        // Spray mist
        isoBoxAt(ctx, sx, sy, w * 0.32, -w * 0.32 * 0.5 - 48, 6, 6, 4, "#A0D8F0", "#90C8E0", "#B0E8FF");
        ctx.globalAlpha = 1;
        // Falling water streams (cascading from bowl to basin)
        ctx.globalAlpha = 0.35;
        for (let s = 0; s < 4; s++) {
          const angle = s * Math.PI * 0.5;
          const streamOx = w * 0.35 + Math.cos(angle) * 8;
          const streamOy = -streamOx * 0.5 - 32 + Math.sin(t + s) * 2;
          isoBoxAt(ctx, sx, sy, streamOx, streamOy, 2, 2, 6, "#70B0D0", "#60A0C0", "#80C0E0");
        }
        ctx.globalAlpha = 1;
      }
      break;
    }

    // ── Crystal Ball ───────────────────────────────────────────────────────
    case "crystal_ball": {
      const t = time * 0.002;
      // Base stand — dark mystical wood
      isoBox(ctx, sx, sy, 14, 14, 4, "#5A3060", "#4A2050", "#6A4070");
      // Stand cradle — cupped ring to hold the orb
      isoBoxAt(ctx, sx, sy, 1, -1 * 0.5 - 4, 12, 12, 4, "#6A4070", "#5A3060", "#7A5080");
      // Inner cradle indent
      isoBoxAt(ctx, sx, sy, 3, -3 * 0.5 - 6, 8, 8, 2, "#4A2850", "#3A1840", "#5A3860");
      // Crystal orb — layered for translucent depth
      isoBoxAt(ctx, sx, sy, 1, -1 * 0.5 - 8, 12, 12, 14, "#B080D0", "#A070C0", "#C090E0");
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 9, 10, 10, 12, "#C090E0", "#B080D0", "#D0A0F0");
      // Inner mist swirl — animated
      const swirl1 = Math.sin(t) * 1.5;
      const swirl2 = Math.cos(t * 1.3) * 1;
      ctx.globalAlpha = 0.5;
      isoBoxAt(ctx, sx, sy, 3, -3 * 0.5 - 12 + swirl1, 8, 6, 4, "#D0A0FF", "#C090EF", "#E0B0FF");
      isoBoxAt(ctx, sx, sy, 4, -4 * 0.5 - 16 + swirl2, 6, 5, 3, "#E0C0FF", "#D0B0EF", "#F0D0FF");
      ctx.globalAlpha = 1;
      // Deep inner glow
      ctx.globalAlpha = 0.4 + Math.sin(t * 1.7) * 0.2;
      isoBoxAt(ctx, sx, sy, 3, -3 * 0.5 - 10, 8, 8, 10, "#C080FF", "#B070EF", "#D090FF");
      ctx.globalAlpha = 1;
      // Surface highlight (specular)
      ctx.globalAlpha = 0.3;
      isoBoxAt(ctx, sx, sy, 2, -2 * 0.5 - 18, 4, 3, 2, "#F0E8FF", "#E8E0F8", "#FFF0FF");
      ctx.globalAlpha = 1;
      // Ambient glow bleed
      ctx.globalAlpha = 0.08;
      isoBoxAt(ctx, sx, sy, -4, -4, 22, 22, 24, "#C080FF", "#B070EF", "#D090FF");
      ctx.globalAlpha = 1;
      break;
    }

    // ── Hearth Lantern ─────────────────────────────────────────────────────
    case "hearth_lantern": {
      const t = time * 0.005;
      // Base plate — heavy, dark metal
      isoBox(ctx, sx, sy, 12, 12, 3, "#706030", "#605020", "#807040");
      // Base rim
      isoBoxAt(ctx, sx, sy, -1, -3 + 0.5, 14, 14, 2, "#907040", "#806030", "#A08050");
      // Cage body — four corner posts (10×10 cage)
      // off(e, m) = { ox: e-m, oy: -(e+m)*0.5 }, then subtract 5 for base height
      isoBoxAt(ctx, sx, sy, 0, -5, 2, 2, 24, "#806030", "#705020", "#907040");
      isoBoxAt(ctx, sx, sy, 10, -10 * 0.5 - 5, 2, 2, 24, "#806030", "#705020", "#907040");
      isoBoxAt(ctx, sx, sy, -10, -10 * 0.5 - 5, 2, 2, 24, "#806030", "#705020", "#907040");
      isoBoxAt(ctx, sx, sy, 0, -(10 + 10) * 0.5 - 5, 2, 2, 24, "#806030", "#705020", "#907040");
      // Glass panels — warm glow when lit
      const glowA = on ? 0.8 + Math.sin(t) * 0.15 : 0.1;
      ctx.globalAlpha = glowA;
      isoBoxAt(ctx, sx, sy, 1, -1 * 0.5 - 6, 10, 10, 20, "#FFD060", "#FFC040", "#FFE080");
      ctx.globalAlpha = 1;
      if (on) {
        // Inner flame
        const flicker = Math.sin(t * 1.5) * 0.8;
        isoBoxAt(ctx, sx, sy, 4, -4 * 0.5 - 10, 4, 4, 8 + flicker, "#FFE040", "#FFD020", "#FFF060");
        isoBoxAt(ctx, sx, sy, 4.5, -4.5 * 0.5 - 12, 3, 3, 5 + flicker, "#FFFFA0", "#FFF080", "#FFFFC0");
        // Glow bleed
        ctx.globalAlpha = 0.12;
        isoBoxAt(ctx, sx, sy, -6, -2, 24, 24, 30, "#FF9020", "#FF8010", "#FFA030");
        ctx.globalAlpha = 1;
      }
      // Top cap — metal pyramid/cone
      isoBoxAt(ctx, sx, sy, 1, -1 * 0.5 - 29, 10, 10, 3, "#907040", "#806030", "#A08050");
      isoBoxAt(ctx, sx, sy, 3, -3 * 0.5 - 32, 6, 6, 3, "#806030", "#705020", "#907040");
      // Hanging loop
      isoBoxAt(ctx, sx, sy, 4, -4 * 0.5 - 35, 4, 4, 4, "#705028", "#604018", "#806038");
      break;
    }

    // ── Pipeline-generated assets ────────────────────────────────────────
    case "box_animated": {
      renderBoxAnimated(ctx, sx, sy - 16, direction, state, time);
      break;
    }
    case "leather_couch": {
      renderLeatherCouch(ctx, sx, sy - 16, direction, state, time);
      break;
    }
    case "macbook": {
      renderMacbook(ctx, sx, sy - 16, direction, state, time);
      break;
    }
    case "spaceship": {
      renderSpaceship(ctx, sx, sy - 16, direction, state, time);
      break;
    }
    case "audi_rs5": {
      renderAudiRS5(ctx, sx, sy - 16, direction, state, time);
      break;
    }
    case "buster_drone": {
      renderBusterDrone(ctx, sx, sy - 16, direction, state, time);
      break;
    }

    // ── Fallback ───────────────────────────────────────────────────────────
    default: {
      // Generic iso block — visible so missing sprites are obvious in dev
      const { w, d } = isoRotate(sx, sy, 20, 20, direction);
      isoBox(ctx, sx, sy, w, d, 24, "#AAAAAA", "#888888", "#999999");
      break;
    }
  }

  ctx.restore();
}

// ─── Rendering Utility ────────────────────────────────────────────────────────

/**
 * Shorthand for ctx.fillRect with color. Avoids repeatedly setting fillStyle
 * for every call within a single furniture render.
 */
function rect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string
): void {
  ctx.fillStyle = color;
  ctx.fillRect(Math.round(x), Math.round(y), Math.max(1, Math.round(w)), Math.max(1, Math.round(h)));
}

// ─── Isometric Drawing Primitives ─────────────────────────────────────────────
//
// Every piece of furniture needs to match the 2:1 isometric tile grid.
// The anchor point (sx, sy) is the SOUTH (front/closest) corner of the base.
//
// Isometric axes from the south corner:
//   East  (width):  per pixel → screen (+1, -0.5)
//   West  (depth):  per pixel → screen (-1, -0.5)
//   Up    (height): per pixel → screen (0, -1)
//
// Direction/rotation swaps which faces are visible and which axis
// width/depth map to, so furniture naturally faces the correct wall.

/**
 * Draw the right (east-facing) side of an isometric box.
 * This face is visible when the camera looks from the south/southwest.
 */
function isoRight(
  ctx: CanvasRenderingContext2D,
  sx: number, sy: number,
  w: number, h: number,
  color: string
): void {
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(sx + w, sy - w * 0.5);
  ctx.lineTo(sx + w, sy - w * 0.5 - h);
  ctx.lineTo(sx, sy - h);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

/**
 * Draw the left (west-facing) side of an isometric box.
 * This face is visible when the camera looks from the south/southeast.
 */
function isoLeft(
  ctx: CanvasRenderingContext2D,
  sx: number, sy: number,
  d: number, h: number,
  color: string
): void {
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(sx - d, sy - d * 0.5);
  ctx.lineTo(sx - d, sy - d * 0.5 - h);
  ctx.lineTo(sx, sy - h);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

/**
 * Draw the top face of an isometric box (a diamond).
 */
function isoTop(
  ctx: CanvasRenderingContext2D,
  sx: number, sy: number,
  w: number, d: number, h: number,
  color: string
): void {
  const ty = sy - h;
  ctx.beginPath();
  ctx.moveTo(sx, ty);                                       // south-top
  ctx.lineTo(sx + w, ty - w * 0.5);                         // east-top
  ctx.lineTo(sx + w - d, ty - w * 0.5 - d * 0.5);          // north-top
  ctx.lineTo(sx - d, ty - d * 0.5);                         // west-top
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

/**
 * Draw a complete isometric box (right face, left face, top).
 * Draws in painter's order: right, left, top.
 *
 * @param sx  Screen X of the south (front) corner
 * @param sy  Screen Y of the south (front) corner
 * @param w   Width in pixels along the east axis
 * @param d   Depth in pixels along the west axis
 * @param h   Height in pixels going straight up
 */
function isoBox(
  ctx: CanvasRenderingContext2D,
  sx: number, sy: number,
  w: number, d: number, h: number,
  topColor: string, leftColor: string, rightColor: string
): void {
  isoRight(ctx, sx, sy, w, h, rightColor);
  isoLeft(ctx, sx, sy, d, h, leftColor);
  isoTop(ctx, sx, sy, w, d, h, topColor);
}

/**
 * Draw an isometric box at an elevated position (raised above the anchor).
 * Useful for items stacked on furniture, tabletops, etc.
 */
function isoBoxAt(
  ctx: CanvasRenderingContext2D,
  sx: number, sy: number,
  ox: number, oy: number,
  w: number, d: number, h: number,
  topColor: string, leftColor: string, rightColor: string
): void {
  isoBox(ctx, sx + ox, sy + oy, w, d, h, topColor, leftColor, rightColor);
}

/**
 * Apply direction rotation to isometric dimensions.
 * Returns adjusted (sx, sy, w, d) so the box faces the correct wall.
 *
 * Direction 0 (SE): default — width goes east, depth goes west
 * Direction 1 (SW): rotated 90° CW — width goes west, depth goes east
 * Direction 2 (NW): rotated 180° — width goes east, depth goes west (back faces shown)
 * Direction 3 (NE): rotated 270° — width goes west, depth goes east
 */
function isoRotate(
  sx: number, sy: number,
  w: number, d: number,
  dir: 0 | 1 | 2 | 3
): { sx: number; sy: number; w: number; d: number; flip: boolean; back: boolean } {
  // dir 0 & 2: width along east (right face wider)
  // dir 1 & 3: swap width and depth (left face wider)
  const flip = dir === 1 || dir === 3;
  // dir 2 & 3: furniture faces AWAY from camera — back is toward viewer.
  // Asymmetric pieces must swap front/back component placement when back=true.
  const back = dir === 2 || dir === 3;
  return {
    sx, sy,
    w: flip ? d : w,
    d: flip ? w : d,
    flip,
    back,
  };
}
